import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { PathwayProgressHeader } from "./PathwayProgressHeader";
import { FlowFooterProvider, PathwayFlowFooter } from "@/components/consultation-flow/ui";
import { pathwayProgressPercent } from "@/lib/pharmacyFirst/engine/pathwayProgress";
import { AlertCircle, CircleHelp, Thermometer, Info, ExternalLink, ChevronDown } from "lucide-react";
import { VitalsModal } from "@/components/PatientDashboardV2/components/VitalsModal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";

import {
  getPathway,
  getPgd,
  getDrug,
  resolveDoseBand,
  calculateDose,
  validateAllConfigs,
  createAuditRecord,
  recordBannerConfirmation,
  recordAnswer,
  recordGateway,
  recordDrugSelection,
  recordPgdExclusionAnswer,
  recordOutcome,
  recordSupplyDetails,
  recordSupplyAcknowledgement,
  recordExtendedSupply,
  recordPatientEmailSent,
  recordPreferenceCapture,
  buildPrefillItemsFromPgd,
  buildSupplyDetailsFromPgd,
  buildAllowedMedicationsFromPgd,
  matchFormulationFromPrescription,
} from "@/lib/pharmacyFirst";
import { sharedLeaflets } from "@/lib/pharmacyFirst/leaflets";
import { PrescriptionModal } from "@/components/PrescriptionModal";
import { matchesDrugSelectionRule } from "@/lib/pharmacyFirst/engine/drugSelection";
import {
  getPgdExclusionCheckPgdId,
  shouldSelectOverridePgd,
  anyCheckTriggered,
  isCheckTriggered,
} from "@/lib/pharmacyFirst/engine/pgdExclusion";
import { resolveEliminationStep, computeEliminationAnswer } from "@/lib/pharmacyFirst/engine/eliminationRouting";
import { resolveConditionRoute } from "@/lib/pharmacyFirst/engine/conditionRouting";
import { resolveDrugResolutionNode } from "@/lib/pharmacyFirst/engine/drugResolutionContext";
import { resolveOutcomeReasonList } from "@/lib/pharmacyFirst/engine/handlers/outcomeContext";
import { useInlineVitalCapture } from "@/lib/pharmacyFirst/engine/useInlineVitalCapture";
import { alarmFlagContext, confirmedBiologicalSex, withDerivedAge } from "@/lib/pharmacyFirst/engine/refinedFlowUi";
import { getIOTFTable } from "@/lib/pharmacyFirst/pgds/aht/iotf-registry";
import { resolveIOTFCutOff } from "@/lib/pharmacyFirst/engine/iotfLookup";

import type {
  PatientInput,
  AuditRecord,
  Pathway,
  PathwayNode,
  OutcomeRef,
  Amendment,
  ChecklistNode as ChecklistNodeShape,
  ConditionRouteContext,
  EliminationLogEntry,
  PreferenceCapture,
} from "@/lib/pharmacyFirst";

import { DemographicConfirmationBanner } from "./DemographicConfirmationBanner";
import {
  YesNoNode,
  ChecklistNode,
  SingleSelectNode,
  MultiSelectNode,
  SeverityNode,
  ScoreNode,
  GatewayAlarmNode,
  GatewayProgressionNode,
  WeightCaptureNode,
  OutcomeNode,
  DrugSelectionNode,
  DrugEliminationNode,
  DrugResolutionNode,
  NumericInputNode,
  DateInputNode,
  FreeTextNode,
  ClinicianAssessmentNode,
  AttestationNode,
  IOTFLookupNode,
  ConfirmationCardNode,
  BMICaptureNode,
  InlineBPCaptureNode,
  PersistentEligibilityPanel,
} from "./nodes";
import type { DrugEliminationAnswer } from "./nodes";
import type { DrugResolutionAnswer } from "./nodes";
import type { NumericInputAnswer } from "./nodes";
import type { DateInputAnswer } from "./nodes";
import type { FreeTextAnswer } from "./nodes";
import type { ClinicianAssessmentAnswer } from "./nodes";
import type { AttestationAnswer } from "./nodes";
import type { IOTFLookupAnswer } from "./nodes";
import type { ConfirmationCardResult } from "./nodes";
import type { BMICaptureAnswer } from "./nodes";
import { ConsultationSummary } from "./ConsultationSummary";
import { AHTTreatmentPlanConfirmModal } from "./AHTTreatmentPlanConfirmModal";
import { AHTPatientEmailModal } from "./AHTPatientEmailModal";
import { AHTAppointmentReviewModal } from "./AHTAppointmentReviewModal";
import { buildAHTTreatmentPlan } from "@/lib/pharmacyFirst/utils/buildAHTTreatmentPlan";
import { generateAHTAppointments } from "@/lib/pharmacyFirst/utils/generateAHTAppointments";
import { calculateTreatmentPlanDates } from "@/lib/pharmacyFirst/utils/calculateTreatmentPlanDates";
import type { AHTWeightTreatmentPlan, AHTDrugId } from "@/lib/ahtTreatmentPlanData";
import type { AHTPlannedAppointment, GenerateAHTAppointmentsInput } from "@/lib/pharmacyFirst/utils/generateAHTAppointments";
import { usePathwayAutoSave } from "@/lib/pharmacyFirst/saveResume";
import type { PathwayEngineSnapshot, AhtChainSnapshot } from "@/lib/pharmacyFirst/saveResume";

interface PathwayEngineProps {
  pathwayId: string;
  patient: PatientInput;
  isDarkMode?: boolean;
  initialContextValues?: Record<string, unknown>;
  onComplete: (auditRecord: AuditRecord) => void;
  onCancel?: () => void;
  /**
   * Called when an outcome links to a calculator (e.g. NEWS2 from emergency).
   * The host opens the calculator in the actions column.
   */
  onOpenCalculator?: (calculatorId: string) => void;
  /**
   * Called when the pharmacist captures a vital reading from inside a PGD
   * pre-supply check (e.g. recording a temperature for the temp >39°C check).
   */
  onSaveVitals?: (vital: any) => void;
  onConsentRefused?: () => void;
  /** One line shown above a single-select node's options, by node id. */
  nodePrefaces?: Record<string, string>;
  onCreateAHTTreatmentPlan?: (plan: AHTWeightTreatmentPlan) => void | Promise<void>;
  onDemographicAmended?: (amendments: Amendment[]) => void;
  patientDisplayName?: string;
  patientEmail?: string | null;
  onUpdatePatientEmail?: (email: string) => Promise<void>;
  patientVitals?: { height?: { value?: number | string; recordedAt?: string }; weight?: { value?: number | string; recordedAt?: string }; bmi?: { value?: number | string; Value?: number | string }; Bmi?: { value?: number | string; Value?: number | string }; bloodPressure?: { systolic?: { value?: number | string }; diastolic?: { value?: number | string } } } | null;
  restoredState?: PathwayEngineSnapshot;
  consultationId?: string;
  companyId?: string;
  templateName?: string;
  hasExistingTemplate?: boolean;
  templateId?: string;
  chainState?: AhtChainSnapshot;
}

type EngineState = "banner" | "consulting" | "prescribing" | "summary" | "complete";

/** Whether a drug_selection node renders an interactive UI (vs auto-advancing) */
function isInteractiveDrugSelection(node: any): boolean {
  return node?.type === "drug_selection" &&
    (node?.variant === "recommend_with_override" || node?.variant === "equal_choice");
}

/**
 * Evaluate a yes_no autoAnswer rule against the current patient.
 * Returns true when every supplied condition has a definite match (AND logic).
 * Supports conditions that can be used together to auto-answer a question.
 * Any unknown patient field (e.g. sex unknown, age unknown) means the
 * question is left for the pharmacist instead of being auto-answered.
 */
function evalAutoAnswer(rule: any, patient: PatientInput): boolean {
  if (!rule?.when) return false;
  const w = rule.when;

  // Check patientIsFemale condition
  if (w.patientIsFemale !== undefined) {
    if (patient.isFemale === undefined) return false;
    if (patient.isFemale !== w.patientIsFemale) return false;
  }

  // Check patientAgeAtLeast condition
  if (w.patientAgeAtLeast !== undefined) {
    if (patient.ageYears === undefined || patient.ageYears === null) return false;
    if (!(patient.ageYears >= w.patientAgeAtLeast)) return false;
  }

  // Check patientAgeUnder condition
  if (w.patientAgeUnder !== undefined) {
    if (patient.ageYears === undefined || patient.ageYears === null) return false;
    if (!(patient.ageYears < w.patientAgeUnder)) return false;
  }

  // Check hasMedication condition — case-insensitive match against currentMedications.
  // Fires when ANY keyword in hasMedication matches ANY entry in currentMedications.
  if (w.hasMedication !== undefined) {
    if (!patient.currentMedications || patient.currentMedications.length === 0) return false;
    const meds = patient.currentMedications.map((m) => m.toLowerCase());
    const matched = w.hasMedication.some((keyword: string) => {
      const kw = keyword.toLowerCase();
      return meds.some((med) => med.includes(kw));
    });
    if (!matched) return false;
  }

  // All conditions matched
  return true;
}

export function PathwayEngine({
  pathwayId,
  patient,
  isDarkMode = false,
  initialContextValues,
  onComplete,
  onCancel,
  onOpenCalculator,
  onSaveVitals,
  onConsentRefused,
  nodePrefaces,
  onCreateAHTTreatmentPlan,
  onDemographicAmended,
  patientDisplayName,
  patientEmail,
  onUpdatePatientEmail,
  patientVitals,
  restoredState,
  consultationId,
  companyId,
  templateName,
  hasExistingTemplate = false,
  templateId,
  chainState,
}: PathwayEngineProps) {
  // Config validation on mount
  useEffect(() => {
    try {
      validateAllConfigs();
    } catch (error) {
      console.error("Config validation failed:", error);
      // In production, this would show an error screen
    }
  }, []);

  const pathway = useMemo(() => {
    try {
      const p = getPathway(pathwayId);
      if (!p) return null;
      const linksMap = (p.links || {});
      // Resolve any linkRefs on nodes/outcomes into clinicalLinks so components
      // can continue to read node.clinicalLinks without changes.
      const nodes = (p.nodes || []).map((n: any) => {
        const nn = { ...n };
        if ((!(Array.isArray(nn.clinicalLinks)) || nn.clinicalLinks.length === 0) && Array.isArray(nn.linkRefs)) {
          nn.clinicalLinks = nn.linkRefs.map((r: string) => linksMap[r]).filter(Boolean);
        }
        return nn;
      });
      const outcomes = Object.fromEntries(
        Object.entries(p.outcomes || {}).map(([k, o]: any) => {
          const oo = { ...o };
          if ((!(Array.isArray(oo.clinicalLinks)) || oo.clinicalLinks.length === 0) && Array.isArray(oo.linkRefs)) {
            oo.clinicalLinks = oo.linkRefs.map((r: string) => linksMap[r]).filter(Boolean);
          }
          return [k, oo];
        }),
      );

      const merged = {
        ...p,
        nodes,
        outcomes,
        leaflets: {
          ...sharedLeaflets,
          ...(p.leaflets || {}),
        },
      } as unknown as Pathway;
      return merged;
    } catch (error) {
      console.error("Failed to load pathway:", error);
      return null;
    }
  }, [pathwayId]);

  // Engine state — skip banner for chained pathways with no demographic fields
  const [engineState, setEngineState] = useState<EngineState>(() => {
    if (restoredState) return restoredState.engineState;
    const hasBannerFields = pathway?.demographicConfiguration?.bannerFields?.length > 0;
    return hasBannerFields ? "banner" : "consulting";
  });
  const [audit, setAudit] = useState<AuditRecord>(() =>
    restoredState?.audit ?? createAuditRecord(pathwayId, patient)
  );
  const [currentNodeId, setCurrentNodeId] = useState<string>(() => {
    if (restoredState?.currentNodeId) {
      const nodeExists = pathway?.nodes?.some((n: any) => n.id === restoredState.currentNodeId);
      const outcomeExists = restoredState.currentNodeId in (pathway?.outcomes ?? {});
      if (nodeExists || outcomeExists) return restoredState.currentNodeId;
    }
    return pathway?.entryNode || "";
  });
  const [answers, setAnswers] = useState<Map<string, unknown>>(() =>
    restoredState?.answers ? new Map(Object.entries(restoredState.answers)) : new Map()
  );
  const [contextValues, setContextValues] = useState<Record<string, unknown>>(
    restoredState?.contextValues ?? initialContextValues ?? {}
  );
  const [history, setHistory] = useState<string[]>(restoredState?.history ?? []);
  const [selectedPgdId, setSelectedPgdId] = useState<string | null>(restoredState?.selectedPgdId ?? null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [demographicBlockMessage, setDemographicBlockMessage] = useState<string | null>(null);
  const [exclusionAnswers, setExclusionAnswers] = useState<
    Record<string, "yes" | "no">
  >(restoredState?.exclusionAnswers ?? {});
  const [prefilledExclusionIds, setPrefilledExclusionIds] = useState<Set<string>>(
    new Set(),
  );
  const [capturedWeightKg, setCapturedWeightKg] = useState<number | null>(restoredState?.capturedWeightKg ?? null);
  const [clinicalRefsOpen, setClinicalRefsOpen] = useState(false);

  const [eligibleDrugIds, setEligibleDrugIds] = useState<string[]>(
    () => restoredState?.eligibleDrugIds ?? pathway?.initialEligibleDrugIds ?? [],
  );
  const [eliminationLog, setEliminationLog] = useState<EliminationLogEntry[]>(restoredState?.eliminationLog ?? []);
  /** nodeId → elimination log length just before that node's answer was applied (used by Back). */
  const eliminationCheckpoints = useRef<Record<string, number>>({});
  /** The scrolling question pane — reset to the top on every step. */
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const inlineVitals = useInlineVitalCapture(onSaveVitals, setAudit);
  /** Speculative preview from the active drug_elimination node before Continue is tapped */
  const [eliminationPreview, setEliminationPreview] = useState<{
    eligibleDrugIds: string[];
    eliminatedDrugIds: string[];
    logEntries: EliminationLogEntry[];
  } | null>(null);
  /** Preview eligible drug IDs shown while a multi-select is in progress. null = no preview. */
  const [previewEligibleDrugIds, setPreviewEligibleDrugIds] = useState<string[] | null>(null);
  const [resolvedDrugId, setResolvedDrugId] = useState<string | null>(restoredState?.resolvedDrugId ?? null);
  const [accumulatedFlags, setAccumulatedFlags] = useState<string[]>(restoredState?.accumulatedFlags ?? []);
  const [managementPlanCaptures, setManagementPlanCaptures] = useState<
    { nodeId: string; planText: string; patientAcknowledged: boolean; capturedAt: string }[]
  >(restoredState?.managementPlanCaptures ?? []);
  const [preferenceCaptures, setPreferenceCaptures] = useState<PreferenceCapture[]>(
    restoredState?.preferenceCaptures ?? []
  );

  const [showAHTTreatmentPlanModal, setShowAHTTreatmentPlanModal] = useState(false);
  const [ahtPlannedAppointments, setAhtPlannedAppointments] = useState<AHTPlannedAppointment[]>([]);
  const [ahtTreatmentPlanCreated, setAhtTreatmentPlanCreated] = useState(false);
  const [showAHTPatientEmailModal, setShowAHTPatientEmailModal] = useState(false);
  const [ahtEmailPlan, setAhtEmailPlan] = useState<AHTWeightTreatmentPlan | null>(null);
  const [monthsSupplied, setMonthsSupplied] = useState<number>(1);
  const [showAhtAppointmentAdjustModal, setShowAhtAppointmentAdjustModal] =
    useState(false);

  const ahtDrugDisplayNames: Record<string, string> = {
    tirzepatide: "Tirzepatide (Mounjaro®)",
    semaglutide: "Semaglutide (Wegovy®)",
    oral_semaglutide: "Oral semaglutide (Wegovy® tablets)",
    foundayo: "Orforglipron (Foundayo® tablets)",
    liraglutide: "Liraglutide (Saxenda®)",
    mysimba: "Naltrexone/bupropion (Mysimba®)",
    orlistat: "Orlistat (Xenical®)",
  };
  const getAhtDrugDisplayName = (drugId: string): string => ahtDrugDisplayNames[drugId] ?? drugId;
  const handleAhtPatientEmailSend = useCallback(() => {
    if (patientEmail) {
      setAudit((prev) =>
        recordPatientEmailSent(prev, patientEmail, new Date().toISOString())
      );
    }
    setShowAHTPatientEmailModal(false);
    setAhtEmailPlan(null);
  }, [patientEmail]);
  const handleAhtPatientEmailSkip = useCallback(() => {
    setShowAHTPatientEmailModal(false);
    setAhtEmailPlan(null);
  }, []);

  /**
   * Record a patient-preference answer for the "Preferences noted" panel on the
   * Select treatment screen. Writes to local state (for the live render and the
   * auto-save snapshot) and to the audit record (for the consultation summary).
   */
  const capturePreference = useCallback(
    (nodeId: string, questionText: string, answerLabel: string) => {
      const record: PreferenceCapture = {
        nodeId,
        questionId: nodeId,
        questionText,
        answerLabel,
        capturedAt: new Date().toISOString(),
      };
      setPreferenceCaptures((prev) => [
        ...prev.filter((c) => c.nodeId !== nodeId),
        record,
      ]);
      setAudit((prev) => recordPreferenceCapture(prev, nodeId, questionText, answerLabel));
    },
    []
  );

  usePathwayAutoSave({
    consultationId: consultationId ?? "",
    companyId: companyId ?? "",
    templateName: templateName ?? "",
    hasExistingTemplate: hasExistingTemplate,
    templateId,
    pathwayId,
    engineState,
    currentNodeId,
    history,
    answers,
    contextValues,
    audit,
    selectedPgdId,
    exclusionAnswers,
    capturedWeightKg,
    eligibleDrugIds,
    eliminationLog,
    resolvedDrugId,
    accumulatedFlags,
    managementPlanCaptures,
    preferenceCaptures,
    chainState,
  });

  /** Patient with mid-flow captures overlaid (e.g. weight). */
  /**
   * BMI already recorded in the patient's vitals. The builder app receives this
   * as a `capturedBmi` prop; here it is derived from `patientVitals` (which the
   * host already supplies), falling back to height/weight when no BMI vital has
   * been stored. Pathways that bypass the in-flow BMI capture node — notably
   * re-initiation — rely on this, otherwise `context.bmi` is undefined and the
   * BMI routers fall through to their GP-referral defaults.
   */
  const capturedBmi: number | undefined = useMemo(() => {
    const num = (v: unknown): number | undefined => {
      if (v === null || v === undefined || v === "") return undefined;
      const n = typeof v === "string" ? parseFloat(v) : (v as number);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };
    const raw = (patientVitals as any)?.bmi ?? (patientVitals as any)?.Bmi;
    const direct = num(raw?.value ?? raw?.Value);
    if (direct !== undefined) return direct;
    const h = num((patientVitals as any)?.height?.value);
    const w = num(capturedWeightKg ?? (patientVitals as any)?.weight?.value);
    if (h !== undefined && w !== undefined) {
      const m = h > 3 ? h / 100 : h; // cm -> m
      const computed = w / (m * m);
      return Number.isFinite(computed) && computed > 0
        ? Math.round(computed * 10) / 10
        : undefined;
    }
    return undefined;
  }, [patientVitals, capturedWeightKg]);

  const effectivePatient: PatientInput = useMemo(
    () => ({
      ...patient,
      ...(capturedWeightKg !== null && capturedWeightKg !== undefined
        ? { weightKg: capturedWeightKg }
        : {}),
      ...(capturedBmi !== undefined ? { bmi: capturedBmi } : {}),
    }),
    [patient, capturedWeightKg, capturedBmi],
  );

  // Seed BMI into pathway context so BMI-dependent routers can evaluate even
  // when the in-flow capture node is bypassed. The capture node still wins:
  // it writes `bmi` via storesToBmi after this runs.
  useEffect(() => {
    if (capturedBmi !== undefined) {
      setContextValues((prev) =>
        prev.bmi === undefined ? { ...prev, bmi: capturedBmi } : prev,
      );
    }
  }, [capturedBmi]);

  // The dose-selection nodes store the pharmacist's choice under
  // `selectedDoseStep`, and their option ids are the PGD's doseStepIds.
  // A pathway with no dose-selection node declares its dose step instead.
  const activeDoseStepId = (contextValues["selectedDoseStep"] ??
    contextValues["doseStepId"] ??
    pathway.fixedDoseStepId) as string | undefined;

  const handleCreateAHTTreatmentPlan = useCallback(() => {
    if (!pathway.id.startsWith("aht-")) return;
    const drugId = audit.supplyDetails?.drugId;
    const pgdId = audit.supplyDetails?.pgdId ?? selectedPgdId;
    if (!drugId || !pgdId) return;

    let pgd: ReturnType<typeof getPgd> | undefined;
    try { pgd = getPgd(pgdId); } catch { return; }
    const reviewSchedule = pgd?.reviewSchedule;
    if (!reviewSchedule) return;

    const today = new Date().toISOString().split("T")[0] ?? new Date().toISOString().substring(0, 10);
    const appointments = generateAHTAppointments({
      initialSupplyDate: today,
      drugId: drugId as GenerateAHTAppointmentsInput["drugId"],
      sectionBReviewDays: reviewSchedule.sectionBReviewDays,
      drugSpecificReviewDays: reviewSchedule.drugSpecificReviewDays,
      drugSpecificReviewLabel: reviewSchedule.drugSpecificReviewLabel,
    });
    setAhtPlannedAppointments(appointments);
    setShowAHTTreatmentPlanModal(true);
  }, [audit.supplyDetails, selectedPgdId, pathway.id]);

  const buildAndSavePlan = async (appointments: AHTPlannedAppointment[]) => {
    if (!audit.supplyDetails) return;
    const drugId = audit.supplyDetails.drugId as AHTDrugId;
    const pgdId = audit.supplyDetails.pgdId ?? selectedPgdId ?? "";

    let pgd: ReturnType<typeof getPgd> | undefined;
    try { pgd = getPgd(pgdId); } catch { return; }
    const reviewSchedule = pgd?.reviewSchedule;
    if (!reviewSchedule) return;

    const today = new Date().toISOString().split("T")[0] ?? new Date().toISOString().substring(0, 10);
    const dates = calculateTreatmentPlanDates({
      actualSupplyDate: today,
      initialSupplyDate: today,
      currentWeek: 1,
      reviewSchedule,
    });

    const weightKg =
      typeof contextValues.weightKg === "number" ? contextValues.weightKg : 0;
    const bmi = typeof contextValues.bmi === "number" ? contextValues.bmi : 0;

    const plan = buildAHTTreatmentPlan({
      patientId: patient.patientId,
      patientName: patientDisplayName ?? patient.patientId,
      consultationId: audit.consultationId,
      pharmacistName: "",
      pharmacistRegistrationNumber: "",
      createdBy: "",
      episodeOrigin: "initialSupply",
      resolvedDrugId: drugId,
      pgdId,
      pgdVersion: audit.supplyDetails.pgdVersion,
      initialDose: audit.supplyDetails.instructionText,
      baselineWeightKg: weightKg,
      baselineWaistCircumferenceCm: null,
      baselineBmi: bmi,
      dates,
      batchNumber: "",
      expiryDate: "",
      brandName: null,
      clinicalRationale: "",
      plannedAppointments: appointments,
    });

    await onCreateAHTTreatmentPlan?.(plan);
    setShowAHTTreatmentPlanModal(false);
    setAhtTreatmentPlanCreated(true);
    setAhtEmailPlan(plan);
    setShowAHTPatientEmailModal(true);
  };

  // Memoize prefill items so identity is stable while the same PGD is selected.
  const prefillItems = useMemo(() => {
    if (!selectedPgdId) return [];
    try {
      return buildPrefillItemsFromPgd(selectedPgdId, effectivePatient, activeDoseStepId);
    } catch (e) {
      console.error("Failed to build prefill items:", e);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPgdId, patient.patientId, capturedWeightKg, activeDoseStepId]);

  const allowedMedications = useMemo(() => {
    if (!selectedPgdId) return [];
    try {
      return buildAllowedMedicationsFromPgd(selectedPgdId, effectivePatient, activeDoseStepId);
    } catch (e) {
      console.error("Failed to build allowed medications:", e);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPgdId, patient.patientId, capturedWeightKg, activeDoseStepId]);

  /**
   * Returns true when a weight_capture node should auto-advance (i.e. weight
   * is not actually needed for the selected PGD, or the patient already has
   * a recorded weight).
   */
  const weightCaptureSatisfied = (): boolean => {
    if (!selectedPgdId) return true;
    try {
      const pgd = getPgd(selectedPgdId);
      if (!pgd.requiresWeightInput) return true;
      const age = effectivePatient.ageYears;
      if (
        age === undefined ||
        age === null ||
        age < pgd.requiresWeightInput.min ||
        age > pgd.requiresWeightInput.max
      ) {
        return true;
      }
      return typeof effectivePatient.weightKg === "number";
    } catch {
      return true;
    }
  };

  const buildConditionRouteCtx = (): ConditionRouteContext => ({
    patient: effectivePatient,
    context: contextValues,
    eligibleDrugIds,
  });

  // An iotf_lookup answers itself from mount for meets/below/no_entry, so it is
  // nothing to step back onto — only pending_data and sex_not_resolvable render
  // a Continue button the pharmacist actually pressed.
  const iotfSelfAnswers = (n: any): boolean => {
    let table;
    try {
      table = getIOTFTable(n.iotfTableId);
    } catch {
      return false;
    }
    const ageMonths =
      effectivePatient.ageYears !== undefined && effectivePatient.ageYears !== null
        ? Math.round(effectivePatient.ageYears * 12)
        : 0;
    const biologicalSex =
      effectivePatient.isFemale === true
        ? "female"
        : effectivePatient.isFemale === false
          ? "male"
          : "other";
    const heightCm = (effectivePatient as any).heightCm;
    const bmi =
      (contextValues.bmi as number) ??
      (typeof effectivePatient.weightKg === "number" &&
        typeof heightCm === "number" &&
        heightCm > 0
        ? effectivePatient.weightKg / ((heightCm / 100) ** 2)
        : 0);
    const result = resolveIOTFCutOff(ageMonths, biologicalSex, bmi, table);
    return (
      result === "meets_threshold" ||
      result === "below_threshold" ||
      result === "no_entry_found"
    );
  };

  const isAutoAdvanceNode = (id: string) => {
    if (!pathway) return false;
    const n = pathway.nodes.find((x) => x.id === id) as any;
    if (!n) return false;
    if (n.type === "flag") return false;
    if (n.type === "condition_route") return true;
    if (n.type === "iotf_lookup") return iotfSelfAnswers(n);
    if (n.type === "drug_selection") return !isInteractiveDrugSelection(n);
    if (n.type === "pgd_exclusion_check") {
      const pgdId = n.pgdIdOverride || selectedPgdId;
      if (pgdId) {
        try { return (getPgd(pgdId).preSupplyChecks || []).length === 0; } catch { /* fall through */ }
      }
    }
    if (n.type === "weight_capture") return weightCaptureSatisfied();
    if (n.type === "yes_no") {
      if (evalAutoAnswer(n.autoAnswer, patient)) {
        return true;
      }
      if (n.id === "q13_pregnant_under16_guard" && patient.isFemale === true && patient.ageYears !== undefined && patient.ageYears >= 16) {
        return true;
      }
    }
    if (n.type === "drug_elimination" && (n.autoAdvance === true || evalAutoAnswer(n.autoAnswer, patient))) {
      return true;
    }
    return false;
  };

  const handleStepBack = () => {
    if (!pathway || history.length === 0) {
      onCancel?.();
      return;
    }
    const newHistory = [...history];
    let target = newHistory.pop() as string;
    while (newHistory.length > 0 && isAutoAdvanceNode(target)) {
      target = newHistory.pop() as string;
    }
    // If we ended up on an auto-advance node with no further history, there
    // is nothing visible to step back to — treat as cancel.
    if (isAutoAdvanceNode(target) && newHistory.length === 0) {
      onCancel?.();
      return;
    }

    // Preserve answers for nodes we're returning to and all nodes before
    const nodesToKeep = new Set([...newHistory, target]);
    const newAnswers = new Map<string, unknown>();
    for (const [id, value] of answers) {
      if (nodesToKeep.has(id)) {
        newAnswers.set(id, value);
      }
    }
    setAnswers(newAnswers);
    setHistory(newHistory);

    // Everything the nodes we just left produced is discarded, including the
    // target's own records — it is about to be answered again, and leaving the
    // old entries in place would layer a second, contradicting record on top.
    const keptNodeIds = new Set(newHistory);
    const contextKeysFor = (n: any): string[] => {
      const keys: string[] = [];
      for (const key of [n?.storesTo, n?.storesToHeight, n?.storesToWeight, n?.storesToBmi]) {
        if (typeof key === "string") keys.push(key);
      }
      if (n?.storesMany && typeof n.storesMany === "object") {
        keys.push(...Object.keys(n.storesMany));
      }
      return keys;
    };

    const keptContextKeys = new Set<string>();
    const staleContextKeys = new Set<string>();
    for (const n of (pathway.nodes ?? []) as any[]) {
      const bucket = keptNodeIds.has(n.id) ? keptContextKeys : staleContextKeys;
      contextKeysFor(n).forEach((k) => bucket.add(k));
    }
    setContextValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of staleContextKeys) {
        // Keep anything a still-answered node wrote, and anything handed in by
        // an earlier pathway in the AHT chain.
        if (keptContextKeys.has(key)) continue;
        if (initialContextValues && key in initialContextValues) continue;
        if (key in next) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setManagementPlanCaptures((prev) =>
      prev.filter((c) => keptNodeIds.has(c.nodeId))
    );
    setPreferenceCaptures((prev) => prev.filter((c) => keptNodeIds.has(c.nodeId)));
    setAccumulatedFlags(
      newHistory.flatMap((id) => {
        const n = pathway.nodes.find((x) => x.id === id) as any;
        if (n?.type !== "flag" || !n.auditFlags) return [];
        return Array.isArray(n.auditFlags) ? n.auditFlags : [n.auditFlags];
      })
    );

    // Prune audit trail: remove gateways from abandoned branches only
    setAudit((prev) => {
      const remainingGateways = prev.gatewaysReached.filter((g) =>
        newHistory.includes((g as any).gatewayId) || (g as any).gatewayId === target
      );
      // Re-derive payment from gateways that are still on the kept path
      const paymentStillTriggered = remainingGateways.some((g) => {
        const node = pathway?.nodes.find((n) => n.id === (g as any).gatewayId) as any;
        return node?.triggersPayment !== false;
      });
      return {
        ...prev,
        answers: prev.answers.filter((a) => keptNodeIds.has(a.nodeId)),
        eliminationLog: (prev.eliminationLog ?? []).filter((e) =>
          keptNodeIds.has(e.nodeId)
        ),
        flagAcknowledgements: (prev.flagAcknowledgements ?? []).filter((f) =>
          keptNodeIds.has(f.nodeId)
        ),
        managementPlanCaptures: (prev.managementPlanCaptures ?? []).filter((c) =>
          keptNodeIds.has(c.nodeId)
        ),
        preferenceCaptures: (prev.preferenceCaptures ?? []).filter((c) =>
          keptNodeIds.has(c.nodeId)
        ),
        confirmationCardAmendments: (prev.confirmationCardAmendments ?? []).filter(
          (c) => keptNodeIds.has(c.nodeId)
        ),
        gatewaysReached: remainingGateways,
        paymentTriggered: paymentStillTriggered,
        paymentTriggeredAt: paymentStillTriggered ? prev.paymentTriggeredAt : undefined,
        paymentTriggeredBy: paymentStillTriggered ? prev.paymentTriggeredBy : undefined,
      };
    });

    // Weight management pathway: replay eligible set and log
    const isWeightManagementPathway =
      Array.isArray(pathway?.initialEligibleDrugIds) &&
      pathway.initialEligibleDrugIds.length > 0;

    if (isWeightManagementPathway) {
      // Everything logged before the target's answer stays — including steps that auto-advanced and so
      // never entered the history — and everything from the target's answer onwards goes. The eligible
      // set is then rebuilt from the kept log alone.
      const checkpoint = eliminationCheckpoints.current[target];
      if (checkpoint !== undefined) {
        const keptLog = eliminationLog.slice(0, checkpoint);
        setEliminationLog(keptLog);
        setEligibleDrugIds(
          pathway.initialEligibleDrugIds!.filter((id) => !keptLog.some((entry) => entry.drugIds.includes(id))),
        );
      } else {
        // No recorded checkpoint for the target: replay from the history-keyed log.
        const newEliminationLog = eliminationLog.filter((entry) => newHistory.includes(entry.nodeId));
        setEliminationLog(newEliminationLog);

        const targetLogEntry = eliminationLog.find((entry) => entry.nodeId === target);
        if (targetLogEntry?.eligibleDrugIdsBeforeNode) {
          setEligibleDrugIds([...targetLogEntry.eligibleDrugIdsBeforeNode]);
        } else {
          const lastKeptEntry = [...eliminationLog]
            .reverse()
            .find((entry) => newHistory.includes(entry.nodeId));

          if (lastKeptEntry) {
            const afterIds = lastKeptEntry.eligibleDrugIdsBeforeNode
              ? lastKeptEntry.eligibleDrugIdsBeforeNode.filter(
                  (id) => !lastKeptEntry.drugIds.includes(id)
                )
              : eligibleDrugIds;
            setEligibleDrugIds([...afterIds]);
          } else {
            setEligibleDrugIds([...pathway.initialEligibleDrugIds]);
          }
        }
      }

      setResolvedDrugId(null);
      setEliminationPreview(null);
      setPreviewEligibleDrugIds(null);
    }

    setErrorMessage(null);
    setCurrentNodeId(target);
  };

  // Auto-advance for nodes that have no UI (drug_selection auto, flag),
  // and for yes_no nodes whose autoAnswer rule is satisfied by patient context
  // (e.g. skip "is the patient pregnant?" for a known-male patient).
  useEffect(() => {
    if (engineState !== "consulting" || !pathway) return;
    const node =
      pathway.nodes.find((n) => n.id === currentNodeId) ||
      pathway.outcomes[currentNodeId];
    if (!node) return;
    // Flag nodes always render and require explicit acknowledgement — the
    // pathway's `autoAdvance` is deliberately ignored here (matches builder).
    if (node.type === "drug_selection" && !isInteractiveDrugSelection(node)) {
      handleAnswer(null);
      return;
    }
    if (node.type === "weight_capture" && weightCaptureSatisfied()) {
      handleAnswer(null);
      return;
    }
    if (node.type === "condition_route") {
      handleAnswer(null);
      return;
    }
    if (node.type === "pgd_exclusion_check") {
      const pgdId = (node as any).pgdIdOverride || selectedPgdId;
      if (pgdId) {
        try {
          const pgd = getPgd(pgdId);
          if ((pgd.preSupplyChecks || []).length === 0) {
            handleAnswer({});
            return;
          }
        } catch { /* pgd not found — let the node render normally */ }
      }
    }
    if (node.type === "yes_no") {
      const n = node as any;
      // Only auto-answer when patient context yields a definite match.
      // If any required field is unknown, leave the question for the pharmacist.
      if (evalAutoAnswer(n.autoAnswer, patient)) {
        handleAnswer(n.autoAnswer.value ? "yes" : "no");
        return; // Prevent further renders of this node
      }
      // Additional auto-answer for q13: females aged 16+
      else if (n.id === "q13_pregnant_under16_guard" && patient.isFemale === true && patient.ageYears !== undefined && patient.ageYears >= 16) {
        handleAnswer(false); // Auto answer "No"
      }
    }
    if (node.type === "drug_elimination") {
      const n = node as any;
      if (n.autoAdvance === true) {
        const { eliminatedDrugIds, logEntries } = computeEliminationAnswer({
          node: n,
          answer: null,
          eligibleDrugIds,
          patient: effectivePatient,
          context: contextValues,
        });
        handleAnswer({
          answer: null,
          eliminatedDrugIds,
          logEntries,
          managementPlan: null,
        } as DrugEliminationAnswer);
        return;
      }
      if (evalAutoAnswer(n.autoAnswer, patient)) {
        const autoVal = n.autoAnswer.value as boolean;
        const { eliminatedDrugIds, logEntries } = computeEliminationAnswer({
          node: n,
          answer: autoVal,
          eligibleDrugIds,
          patient: effectivePatient,
          context: contextValues,
        });
        handleAnswer({
          answer: autoVal,
          eliminatedDrugIds,
          logEntries,
          managementPlan: null,
        } as DrugEliminationAnswer);
        return;
      }
    }
  }, [currentNodeId, engineState]);

  // Record gateway passage when entering a gateway node
  useEffect(() => {
    if (!pathway) return;
    const node = pathway.nodes.find((n) => n.id === currentNodeId);
    if (!node || (node.type !== "gateway_alarm" && node.type !== "gateway_progression")) {
      return;
    }
    // Mark gateway as reached and record payment if triggered
    const n = node as any;
    setAudit((prev) =>
      recordGateway(
        prev,
        currentNodeId,
        node.type === "gateway_alarm" ? "alarm" : "progression",
        n.triggersPayment !== false,
      ),
    );
  }, [currentNodeId]);

  // Pre-fill exclusion answers when entering a pgd_exclusion node
  useEffect(() => {
    if (!pathway) return;
    const node = pathway.nodes.find((n) => n.id === currentNodeId);
    if (!node || node.type !== "pgd_exclusion_check") {
      setExclusionAnswers({});
      setPrefilledExclusionIds(new Set());
      return;
    }
    const pgdId = getPgdExclusionCheckPgdId(node, selectedPgdId);
    if (!pgdId) {
      setExclusionAnswers({});
      setPrefilledExclusionIds(new Set());
      return;
    }
    let pgd;
    try {
      pgd = getPgd(pgdId);
    } catch {
      setExclusionAnswers({});
      setPrefilledExclusionIds(new Set());
      return;
    }
    const prefillMap = pathway.exclusionPrefillMap || {};
    const next: Record<string, "yes" | "no"> = {};
    const prefilled = new Set<string>();

    // First, restore any previously answered exclusion questions
    const prevAnswers = answers.get(currentNodeId);
    if (prevAnswers && typeof prevAnswers === "object") {
      Object.assign(next, prevAnswers);
    }

    for (const check of pgd.preSupplyChecks) {
      // Skip if already answered (e.g., from going back)
      if (next[check.id]) continue;

      // Universal consent prefill: if the pharmacist confirmed consent at the
      // demographic banner stage, answer the check from that. Inclusion
      // criteria are phrased positively ("Has the patient provided informed
      // consent?") and exclusion criteria negatively ("Consent refused?"), so
      // the safe answer flips with `direction`. Never pre-empt a check that
      // names its own source — that source is more specific than the banner.
      if (
        contextValues.consentObtained === true &&
        !(check as any).autoAnswerFrom &&
        !prefillMap[check.id] &&
        /consent/i.test(check.id + " " + (check.question || ""))
      ) {
        next[check.id] =
          (check as any).direction === "exclude_on_no" ? "yes" : "no";
        prefilled.add(check.id);
        continue;
      }
      const rule = prefillMap[check.id];
      if (!rule) continue;
      const ctxVal = contextValues[rule.from];
      if (ctxVal === undefined) continue;
      const target =
        rule.equals !== undefined
          ? rule.equals
          : rule.trueWhenEquals !== undefined
            ? rule.trueWhenEquals
            : true;
      next[check.id] = ctxVal === target ? "yes" : "no";
      prefilled.add(check.id);
    }

    // PGD-level pre-answering: each preSupplyCheck may declare
    // `autoAnswerFrom`, either a known derived key (age/BMI bands) or the name
    // of a context value stored earlier in the consultation. Checks whose
    // source is unavailable are left blank for the pharmacist to answer.
    for (const check of pgd.preSupplyChecks as any[]) {
      if (!check.autoAnswerFrom) continue;
      if (next[check.id]) continue;
      const age = (effectivePatient as any)?.ageYears;
      const bmi = contextValues["bmi"];
      const comorbidities = contextValues["q2aComorbiditySelections"];
      const setAns = (v: "yes" | "no") => {
        next[check.id] = v;
        prefilled.add(check.id);
      };

      switch (check.autoAnswerFrom) {
        case "age_under_75":
          if (typeof age === "number") setAns(age < 75 ? "no" : "yes");
          break;
        case "age_under_12":
          if (typeof age === "number") setAns(age >= 12 ? "no" : "yes");
          break;
        case "age_under_18":
          if (typeof age === "number") setAns(age < 18 ? "yes" : "no");
          break;
        case "age_18_to_84":
          if (typeof age === "number") setAns(age >= 18 && age <= 84 ? "yes" : "no");
          break;
        case "age_16_to_64":
          if (typeof age === "number") setAns(age >= 16 && age <= 64 ? "yes" : "no");
          break;
        case "face_to_face_confirmed": {
          const f2fAnswered = (audit.answers ?? []).some(
            (a: any) =>
              a.nodeType === "yes_no" &&
              (String(a.nodeId).includes("f2f") || String(a.nodeId).includes("face")) &&
              a.answer === "yes",
          );
          if (f2fAnswered) setAns("no");
          break;
        }
        case "bmiBelowThreshold":
          if (typeof bmi === "number") setAns(bmi < 27 ? "yes" : "no");
          break;
        case "bmiBelowThreshold28":
          if (typeof bmi === "number") setAns(bmi < 28 ? "yes" : "no");
          break;
        case "bmiBelowFloor":
          if (typeof bmi === "number") setAns(bmi < 22 ? "yes" : "no");
          break;
        case "bmiBelowFloor225":
          if (typeof bmi === "number") setAns(bmi < 22.5 ? "yes" : "no");
          break;
        case "bmiInLowRangeNoComorbidity":
          if (typeof bmi === "number" && Array.isArray(comorbidities))
            setAns(bmi >= 27 && bmi < 30 && comorbidities.length === 0 ? "yes" : "no");
          break;
        case "bmiInLowRangeNoComorbidity28":
          if (typeof bmi === "number" && Array.isArray(comorbidities))
            setAns(bmi >= 28 && bmi < 30 && comorbidities.length === 0 ? "yes" : "no");
          break;
        default:
          break;
      }

      // Context-key fallback: treat any other autoAnswerFrom value as a
      // contextValues key stored by a prior node (storesTo / storesMany).
      if (!next[check.id]) {
        const ctxVal = contextValues[check.autoAnswerFrom];
        if (ctxVal !== undefined) {
          const asAnswer =
            ctxVal === true || ctxVal === "yes"
              ? "yes"
              : ctxVal === false || ctxVal === "no"
                ? "no"
                : undefined;
          if (asAnswer !== undefined) setAns(asAnswer);
        }
      }
    }

    setExclusionAnswers((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length &&
        prevKeys.every((k) => prev[k] === next[k])
      ) {
        return prev;
      }
      return next;
    });
    setPrefilledExclusionIds((prev) => {
      if (prev.size === prefilled.size && [...prefilled].every((id) => prev.has(id))) {
        return prev;
      }
      return prefilled;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeId, selectedPgdId]);

  // Rebuild supply details when capturedWeightKg updates and we're already on
  // a supply outcome — covers the case where the pre-loop build was deferred
  // because the weight state hadn't propagated yet.
  useEffect(() => {
    if (!selectedPgdId || !pathway) return;
    const outcome = pathway.outcomes[currentNodeId];
    if (outcome?.outcomeType !== "supply") return;
    try {
      const pgd = getPgd(selectedPgdId);
      if (resolveDoseBand(pgd, effectivePatient, activeDoseStepId) === null) return;
      const supplyDetails = buildSupplyDetailsFromPgd(selectedPgdId, effectivePatient, undefined, false, activeDoseStepId);
      setAudit((prev) => recordSupplyDetails(prev, supplyDetails));
    } catch {
      // Still not resolvable — will surface as missing prescription details
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capturedWeightKg, currentNodeId, selectedPgdId]);

  // Every step change (and every validation error) starts at the top. Without
  // this, pressing Next on a long question leaves the next question scrolled
  // past its own heading — or hides the error the user needs to read.
  useEffect(() => {
    const pane = contentScrollRef.current;
    if (!pane) return;
    pane.scrollTo({ top: 0, behavior: "smooth" });
    // When the engine is embedded in a host template the overflow can belong to
    // an ancestor instead, so reset the nearest scrolling ancestor too. Stop at
    // the document to avoid yanking the whole page when nothing above scrolls.
    for (let el = pane.parentElement; el && el !== document.body; el = el.parentElement) {
      const overflowY = getComputedStyle(el).overflowY;
      if (overflowY !== "auto" && overflowY !== "scroll") continue;
      if (el.scrollTop > 0) el.scrollTo({ top: 0, behavior: "smooth" });
      break;
    }
  }, [currentNodeId, errorMessage, engineState]);

  if (!pathway) {
    return (
      <div className="p-4 space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load pathway. Please try again.</AlertDescription>
        </Alert>
        {onCancel && (
          <Button onClick={onCancel} variant="outline" className="w-full">
            Back
          </Button>
        )}
      </div>
    );
  }

  const isAutoAdvanceType = (n: any) => {
    if (!n) return false;
    if (n.type === "flag") return false;
    if (n.type === "condition_route") return true;
    if (n.type === "drug_selection") return !isInteractiveDrugSelection(n);
    if (n.type === "weight_capture") return weightCaptureSatisfied();
    if (n.type === "yes_no" && evalAutoAnswer(n.autoAnswer, patient)) {
      return true;
    }
    if (n.type === "drug_elimination" && (n.autoAdvance === true || evalAutoAnswer(n.autoAnswer, patient))) {
      return true;
    }
    return false;
  };
  const answeredVisibleCount = Array.from(answers.keys()).filter((id) => {
    const n = pathway.nodes.find((x) => x.id === id);
    return n ? !isAutoAdvanceType(n) : true;
  }).length;
  // Step numbers continue from the earlier chain steps, so a refined flow numbers its cards without restarting at 1.
  const answeredNodesCount = answeredVisibleCount + (pathway.refinedFlowUi?.stepOffset ?? 0);
  const progressPercent = pathwayProgressPercent(pathway.nodes, currentNodeId, isAutoAdvanceType, answeredVisibleCount);

  const currentNode =
    pathway.nodes.find((n) => n.id === currentNodeId) ||
    pathway.outcomes[currentNodeId] ||
    null;
  const isOutcome = currentNode?.type === "outcome";
  const outcomeNode = isOutcome
    ? pathway.outcomes[currentNodeId] || currentNode
    : null;

  const isWeightManagementPathway =
    Array.isArray(pathway?.initialEligibleDrugIds) &&
    pathway.initialEligibleDrugIds.length > 0;

  const hasPassedQ2a =
    answers.has("Q2a_comorbidities_checklist") ||
    answers.has("Q2b_hba1c_entry");

  const showPersistentEligibilityPanel =
    isWeightManagementPathway &&
    hasPassedQ2a &&
    engineState === "consulting";

  const showEmptyDrugSetAlert =
    isWeightManagementPathway &&
    hasPassedQ2a &&
    eligibleDrugIds.length === 0 &&
    engineState === "consulting";

  /**
   * Human-readable labels for each drug in the initial eligible set.
   * Falls back to drug ID if drug not yet registered.
   */
  const drugLabels = React.useMemo<Record<string, string>>(() => {
    const labels: Record<string, string> = {};
    const ids = pathway?.initialEligibleDrugIds ?? [];
    for (const id of ids) {
      try {
        labels[id] = getDrug(id).name;
      } catch {
        labels[id] = id;
      }
    }
    return labels;
  }, [pathway?.id]);

  // Resolve pathway-level clinical links to show in the header (NICE CKS etc.)
  const headerLinks = React.useMemo(() => {
    if (!pathway) return [] as any[];
    const linksMap = (pathway.links as any) || {};
    const refs: string[] = (pathway as any).clinicalNoteLinkRefs || [];
    const resolved: any[] = [];
    // Prefer explicit clinicalNoteLinkRefs when provided
    for (const r of refs) {
      const l = linksMap[r];
      if (l) resolved.push(l);
    }
    // Fallback: gather node-level clinicalLinks
    if (resolved.length === 0) {
      const seen = new Set<string>();
      for (const n of pathway.nodes) {
        const cls = (n as any).clinicalLinks;
        if (Array.isArray(cls)) {
          for (const cl of cls) {
            if (!seen.has(cl.url)) {
              seen.add(cl.url);
              resolved.push(cl);
            }
          }
        }
      }
    }
    return resolved;
  }, [pathway]);

  const getRoutingTarget = (target: string | OutcomeRef): string => {
    if (typeof target === "string") return target;
    return target.outcomeId;
  };

  /** Apply outcomeOverrides — substitutes a supply outcome with another id. */
  const applyOutcomeOverride = (incomingOutcomeId: string): string => {
    const incoming = pathway.outcomes[incomingOutcomeId];
    if (!incoming || incoming.outcomeType !== "supply") return incomingOutcomeId;
    const overrides = pathway.outcomeOverrides || [];
    for (const ov of overrides) {
      const matches = Object.entries(ov.when).every(
        ([k, v]) => (contextValues[k] as any) === v,
      );
      if (matches) return ov.outcomeId;
    }
    return incomingOutcomeId;
  };

  const isWeightCaptureSatisfiedForPgd = (pgdId: string | null): boolean => {
    if (!pgdId) return true;
    try {
      const pgd = getPgd(pgdId);
      if (!pgd.requiresWeightInput) return true;
      const age = effectivePatient.ageYears;
      if (age === undefined || age === null || age < pgd.requiresWeightInput.min || age > pgd.requiresWeightInput.max) return true;
      return typeof effectivePatient.weightKg === "number";
    } catch {
      return true;
    }
  };

  const advanceTo = (nextId: string, pgdIdOverride?: string, pendingContext?: Record<string, unknown>, eligibleDrugIdsOverride?: string[]) => {
    let target = nextId;
    // If the engine is about to enter a supply outcome, see if an override applies
    const out = pathway.outcomes[target];
    if (out && out.outcomeType === "supply") {
      target = applyOutcomeOverride(target);
    }
    // If the target is a supply outcome and we have a selected PGD,
    // populate supply details NOW so the OutcomeNode can display the drug name.
    // Only attempt if a dose band can actually be resolved — weight may not yet
    // be reflected in effectivePatient if setCapturedWeightKg just fired.
    const targetOutcome = pathway.outcomes[target];
    const effectivePgdId = pgdIdOverride || selectedPgdId;
    if (targetOutcome?.outcomeType === "supply" && effectivePgdId) {
      try {
        const pgdForCheck = getPgd(effectivePgdId);
        if (resolveDoseBand(pgdForCheck, effectivePatient, activeDoseStepId) !== null) {
          const supplyDetails = buildSupplyDetailsFromPgd(effectivePgdId, effectivePatient, undefined, false, activeDoseStepId);
          setAudit((prev) => recordSupplyDetails(prev, supplyDetails));
        }
      } catch {
        // Silently skip — supply details will be built when outcome renders with updated patient data
      }
    }

    // Skip nodes that would auto-answer to prevent render flash
    let resolvedPgdId: string | null = pgdIdOverride || selectedPgdId || null;
    // Accumulates context values set by storesTo fields during skip — merged into resolvedContext for drug matching
    let accumulatedContext: Record<string, unknown> = pendingContext ? { ...pendingContext } : {};
    // Running eligible-drug set across chained auto-advance eliminations within this single hop sequence.
    // Without this, each elimination step would read the stale pre-loop eligibleDrugIds state, so only the
    // LAST auto-eliminated drug of the chain would actually stick when setEligibleDrugIds finally flushes.
    let pendingEligibleDrugIds: string[] | null = eligibleDrugIdsOverride ?? null;
    while (target && !pathway.outcomes[target]) {
      const node = pathway.nodes.find((n) => n.id === target);
      if (!node) break;

      if (node.type === "yes_no") {
        const n = node as any;
        if (evalAutoAnswer(n.autoAnswer, effectivePatient)) {
          const autoAnswer = n.autoAnswer.value ? "yes" : "no";
          setAudit((prev) => recordAnswer(prev, target, "yes_no", autoAnswer));
          if (n.storesTo) {
            const val = n.autoAnswer.value as boolean;
            setContextValues((p) => ({ ...p, [n.storesTo]: val }));
            accumulatedContext = { ...accumulatedContext, [n.storesTo]: val };
          }
          target = autoAnswer === "yes" ? getRoutingTarget(n.yes) : getRoutingTarget(n.no);
        } else {
          break;
        }
      } else if (node.type === "condition_route") {
        const n = node as any;
        const ctx: ConditionRouteContext = {
          patient: effectivePatient,
          context: { ...contextValues, ...accumulatedContext },
          eligibleDrugIds: pendingEligibleDrugIds ?? eligibleDrugIds,
        };
        const resolved = resolveConditionRoute(n, ctx);
        if (n.storesTo && n.storedValue) {
          const val = n.storedValue(ctx);
          setContextValues((p) => ({ ...p, [n.storesTo]: val }));
          accumulatedContext = { ...accumulatedContext, [n.storesTo]: val };
        }
        target = getRoutingTarget(resolved);
      } else if (node.type === "drug_selection" && !isInteractiveDrugSelection(node)) {
        const n = node as any;
        let matched = false;
        const resolvedContext = { ...contextValues, ...accumulatedContext };
        for (let i = 0; i < n.rules.length; i++) {
          const rule = n.rules[i];
          if (matchesDrugSelectionRule(rule, effectivePatient, resolvedContext)) {
            if (rule.cannotSupplyOutcomeId) {
              target = getRoutingTarget({ outcomeId: rule.cannotSupplyOutcomeId });
            } else {
              const pgdId = rule.pgdId;
              resolvedPgdId = pgdId;
              setSelectedPgdId(pgdId);
              setAudit((prev) => {
                const pgd = getPgd(pgdId);
                return recordDrugSelection(prev, pgdId, pgd.version, i);
              });
              target = n.next;
            }
            matched = true;
            break;
          }
        }
        if (!matched) break;
      } else if (node.type === "weight_capture" && isWeightCaptureSatisfiedForPgd(resolvedPgdId)) {
        const n = node as any;
        target = getRoutingTarget(n.next);
      } else if (node.type === "drug_elimination" && (node as any).autoAdvance === true) {
        const n = node as any;
        const eligibleBefore = pendingEligibleDrugIds ?? eligibleDrugIds;
        const { eliminatedDrugIds, logEntries } = computeEliminationAnswer({
          node: n,
          answer: null,
          eligibleDrugIds: eligibleBefore,
          patient: effectivePatient,
          context: { ...contextValues, ...accumulatedContext },
        });
        const stepResult = resolveEliminationStep(
          n,
          { answer: null, eliminatedDrugIds, logEntries },
          eligibleBefore,
          target,
          new Date().toISOString(),
        );
        pendingEligibleDrugIds = stepResult.remainingDrugIds;
        setEligibleDrugIds(stepResult.remainingDrugIds);
        setEliminationLog((prev) => [...prev, ...stepResult.logEntries]);
        setAudit((prev) => ({
          ...prev,
          eliminationLog: [...(prev.eliminationLog ?? []), ...stepResult.logEntries],
        }));
        target = getRoutingTarget(stepResult.nextTarget);
      } else if (node.type === "drug_elimination" && evalAutoAnswer((node as any).autoAnswer, effectivePatient)) {
        const n = node as any;
        const autoVal = n.autoAnswer.value as boolean;
        const eligibleBefore = pendingEligibleDrugIds ?? eligibleDrugIds;
        const { eliminatedDrugIds, logEntries } = computeEliminationAnswer({
          node: n,
          answer: autoVal,
          eligibleDrugIds: eligibleBefore,
          patient: effectivePatient,
          context: { ...contextValues, ...accumulatedContext },
        });
        const stepResult = resolveEliminationStep(
          n,
          { answer: autoVal, eliminatedDrugIds, logEntries },
          eligibleBefore,
          target,
          new Date().toISOString(),
        );
        pendingEligibleDrugIds = stepResult.remainingDrugIds;
        setEligibleDrugIds(stepResult.remainingDrugIds);
        setEliminationLog((prev) => [...prev, ...stepResult.logEntries]);
        setAudit((prev) => ({
          ...prev,
          eliminationLog: [...(prev.eliminationLog ?? []), ...stepResult.logEntries],
        }));
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: autoVal }));
          accumulatedContext = { ...accumulatedContext, [n.storesTo]: autoVal };
        }
        target = getRoutingTarget(stepResult.nextTarget);
      } else {
        break;
      }
    }

    setHistory((h) => [...h, currentNodeId]);
    setCurrentNodeId(target);
  };

  const handleAnswer = (answer: unknown) => {
    setEliminationPreview(null);
    setPreviewEligibleDrugIds(null);
    if (!currentNode) return;

    eliminationCheckpoints.current[currentNodeId] = eliminationLog.length;

    const newAnswers = new Map(answers);
    newAnswers.set(currentNodeId, answer);
    setAnswers(newAnswers);

    setAudit((prev) =>
      recordAnswer(prev, currentNodeId, currentNode.type, answer)
    );

    let nextNodeId: string | null = null;
    // Context written by this answer. Must be merged into the chain handoff
    // snapshot below, because `contextValues` is still the stale pre-answer
    // closure value when onComplete fires (React state has not flushed yet).
    let pendingChainCtx: Record<string, unknown> | undefined;

    switch (currentNode.type) {
      case "yes_no": {
        const n = currentNode as any;
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo) {
          const val = answer === "yes";
          setContextValues((p) => ({ ...p, [n.storesTo]: val }));
          pendingCtx = { [n.storesTo]: val };
        }
        const target = answer === "yes" ? n.yes : n.no;
        nextNodeId = getRoutingTarget(target);
        pendingChainCtx = pendingCtx;
        if (nextNodeId && pendingCtx && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "checklist": {
        const n = currentNode as any;
        const selectedIds = answer as string[];
        const noneSelected = selectedIds.length === 0;
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: !noneSelected }));
          pendingCtx = { [n.storesTo]: !noneSelected };
        }
        if (n.storesMany && typeof n.storesMany === "object") {
          const patch: Record<string, unknown> = {};
          for (const [key, derive] of Object.entries(
            n.storesMany as Record<string, (ids: string[]) => unknown>,
          )) {
            patch[key] = derive(selectedIds);
          }
          setContextValues((p) => ({ ...p, ...patch }));
          pendingCtx = { ...(pendingCtx ?? {}), ...patch };
        }
        const target = noneSelected ? n.noneSelected : n.anySelected;
        if (!target) {
          setErrorMessage(`Checklist node ${currentNodeId} has no routing for ${noneSelected ? "noneSelected" : "anySelected"}`);
          return;
        }
        nextNodeId = getRoutingTarget(target);
        pendingChainCtx = pendingCtx;
        if (nextNodeId && pendingCtx && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "single_select": {
        const n = currentNode as any;
        const selectedId = answer as string;
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: selectedId }));
          pendingCtx = { [n.storesTo]: selectedId };
        }
        if (n.capturePreference) {
          const optionLabel =
            n.options.find((o: { id: string; label: string }) => o.id === selectedId)?.label ??
            selectedId;
          capturePreference(currentNodeId, n.text, optionLabel);
        }
        const target = n.routing[selectedId];
        if (!target) {
          setErrorMessage(`No routing for option ${selectedId}`);
          return;
        }
        nextNodeId = getRoutingTarget(target);
        pendingChainCtx = pendingCtx;
        if (nextNodeId && pendingCtx && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "multi_select": {
        const n = currentNode as any;
        const selectedIds = answer as string[];
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: selectedIds }));
          pendingCtx = { [n.storesTo]: selectedIds };
        }
        if (n.storesMany && typeof n.storesMany === "object") {
          const patch: Record<string, unknown> = {};
          for (const [key, derive] of Object.entries(
            n.storesMany as Record<string, (ids: string[]) => unknown>,
          )) {
            patch[key] = derive(selectedIds);
          }
          setContextValues((p) => ({ ...p, ...patch }));
          pendingCtx = { ...(pendingCtx ?? {}), ...patch };
        }
        if (n.capturePreference) {
          const items: { id: string; text: string }[] = n.groups
            ? n.groups.flatMap((g: { items: { id: string; text: string }[] }) => g.items)
            : (n.items ?? []);
          const answerLabel = selectedIds
            .map((id) => items.find((item) => item.id === id)?.text ?? id)
            .join(", ");
          capturePreference(currentNodeId, n.text, answerLabel);
        }
        if (typeof n.minRequired === "number" && (n.thresholdMet || n.thresholdNotMet)) {
          const target = selectedIds.length >= n.minRequired ? n.thresholdMet : n.thresholdNotMet;
          nextNodeId = getRoutingTarget(target);
        } else if (typeof n.routing === "function") {
          const target = n.routing(selectedIds, patient);
          nextNodeId = getRoutingTarget(target);
        } else {
          setErrorMessage("Multi-select node missing routing");
          return;
        }
        pendingChainCtx = pendingCtx;
        if (nextNodeId && pendingCtx && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "severity": {
        const n = currentNode as any;
        const severity = answer as string;
        let target = n[severity];
        if (target && typeof target === "object" && !target.outcomeId) {
          target = contextValues.isFollowUp ? target.followup : target.initial;
        }
        nextNodeId = getRoutingTarget(target);
        break;
      }

      case "score": {
        const n = currentNode as any;
        const scoreAnswer = answer as { totalScore: number; selectedIds: string[] };
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: scoreAnswer.totalScore }));
          pendingCtx = { [n.storesTo]: scoreAnswer.totalScore };
        }
        if (typeof n.routing === "function") {
          const target = n.routing(scoreAnswer.totalScore, patient, contextValues);
          nextNodeId = getRoutingTarget(target);
        } else {
          const scoreKey =
            scoreAnswer.totalScore <= 1
              ? "score_0_or_1"
              : scoreAnswer.totalScore <= 3
                ? "score_2_or_3"
                : "score_4_or_5";
          let target = n.routing?.[scoreKey];
          if (target && typeof target === "object" && !target.outcomeId) {
            target = contextValues.isFollowUp ? target.followup : target.initial;
          }
          nextNodeId = getRoutingTarget(target);
        }
        pendingChainCtx = pendingCtx;
        if (nextNodeId && pendingCtx && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "drug_selection": {
        const n = currentNode as any;
        // Auto variant
        if (!isInteractiveDrugSelection(n)) {
          for (let i = 0; i < n.rules.length; i++) {
            const rule = n.rules[i];
            if (matchesDrugSelectionRule(rule, patient, contextValues)) {
              if (rule.cannotSupplyOutcomeId) {
                const outcomeRef = { outcomeId: rule.cannotSupplyOutcomeId };
                nextNodeId = getRoutingTarget(outcomeRef);
                break;
              }
              if (!rule.pgdId) {
                setErrorMessage("No matching PGD found for this patient");
                return;
              }
              setSelectedPgdId(rule.pgdId);
              setAudit((prev) => {
                const pgd = getPgd(rule.pgdId);
                return recordDrugSelection(prev, rule.pgdId, pgd.version, i);
              });
              nextNodeId = n.next;
              break;
            }
          }
          if (!nextNodeId) {
            setErrorMessage("No matching PGD found for this patient");
            return;
          }
        } else {
          // Interactive — `answer` is { pgdId, ruleIndex, overrideReason?, recommendedPgdId? }
          const a = answer as any;
          setSelectedPgdId(a.pgdId);
          setAudit((prev) => {
            const pgd = getPgd(a.pgdId);
            return recordDrugSelection(prev, a.pgdId, pgd.version, a.ruleIndex, {
              overridden: !!a.overrideReason,
              overrideReason: a.overrideReason,
              recommendedPgdId: a.recommendedPgdId,
            });
          });
          nextNodeId = n.next;
        }
        break;
      }

      case "pgd_exclusion_check": {
        const n = currentNode as any;
        const pgdId = getPgdExclusionCheckPgdId(n, selectedPgdId);
        if (!pgdId) {
          setErrorMessage("No PGD selected");
          return;
        }
        const presupplyChecks = (() => {
          try { return getPgd(pgdId).preSupplyChecks || []; } catch { return []; }
        })();
        // Sequential checks must respect each check's `direction`: inclusion
        // criteria (exclude_on_no) are triggered by "no", not "yes".
        const anyExclusionTriggered =
          n.variant !== "sequential"
            ? (answer as string[]).length > 0
            : anyCheckTriggered(
                presupplyChecks,
                answer as Record<string, "yes" | "no">,
              );
        // The per-check verdicts are the evidence the consultation summary
        // reports from, so persist each one. Recording only the node's combined
        // answer leaves every PGD-derived summary row unresolved.
        const checkVerdicts: { check: any; triggered: boolean }[] =
          n.variant !== "sequential"
            ? presupplyChecks
                .filter((c) => (answer as string[]).includes(c.id))
                .map((check) => ({ check, triggered: true }))
            : presupplyChecks
                .filter((c) => (answer as Record<string, string>)[c.id])
                .map((check) => ({
                  check,
                  triggered: isCheckTriggered(
                    check,
                    (answer as Record<string, "yes" | "no">)[check.id],
                  ),
                }));
        if (checkVerdicts.length > 0) {
          setAudit((prev) =>
            checkVerdicts.reduce(
              (acc, { check, triggered }) =>
                recordPgdExclusionAnswer(
                  acc,
                  check.id,
                  pgdId,
                  check.id.startsWith("int-") ? "interaction" : "exclusion",
                  check.question,
                  triggered,
                ),
              prev,
            ),
          );
        }
        let activePgdId = pgdId;
        if (shouldSelectOverridePgd(n, anyExclusionTriggered)) {
          setSelectedPgdId(n.pgdIdOverride);
          activePgdId = n.pgdIdOverride;
        }
        nextNodeId = getRoutingTarget(
          anyExclusionTriggered ? n.anyTriggered : n.allCleared,
        );
        if (nextNodeId) {
          advanceTo(nextNodeId, activePgdId);
          return;
        }
        break;
      }

      case "flag": {
        const n = currentNode as any;
        if (n.auditFlags) {
          const flags: string[] = Array.isArray(n.auditFlags) ? n.auditFlags : [n.auditFlags];
          setAccumulatedFlags((prev) => [...prev, ...flags]);
          // Flag nodes always require explicit acknowledgement, so always
          // record it (matches builder — no autoAdvance gate).
          setAudit((prev) => ({
            ...prev,
            flagAcknowledgements: [
              ...(prev.flagAcknowledgements ?? []),
              ...flags.map((f: string) => ({
                nodeId: currentNodeId,
                flagKey: f,
                acknowledgedAt: new Date().toISOString(),
              })),
            ],
          }));
        }
        nextNodeId = n.next;
        break;
      }

      case "condition_route": {
        const n = currentNode as any;
        const ctx = buildConditionRouteCtx();
        const resolved = resolveConditionRoute(n, ctx);
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo && n.storedValue) {
          const val = n.storedValue(ctx);
          setContextValues((p) => ({ ...p, [n.storesTo]: val }));
          pendingCtx = { [n.storesTo]: val };
          if (n.linkedVitalType && onSaveVitals && val != null) {
            onSaveVitals({ type: n.linkedVitalType, data: { value: String(val) }, timestamp: new Date().toISOString() });
          }
        }
        nextNodeId = getRoutingTarget(resolved);
        pendingChainCtx = pendingCtx;
        if (nextNodeId && pendingCtx && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "weight_capture": {
        const n = currentNode as any;
        if (typeof answer === "number" && Number.isFinite(answer) && answer > 0) {
          setCapturedWeightKg(answer);
          if (onSaveVitals) {
            onSaveVitals({ type: "weight", data: { value: String(answer), unit: "kg" }, timestamp: new Date().toISOString() });
          }
        }
        nextNodeId = n.next;
        break;
      }

      case "drug_elimination": {
        const n = currentNode as any;
        const a = answer as DrugEliminationAnswer;
        const stepResult = resolveEliminationStep(
          n,
          {
            answer: a.answer,
            eliminatedDrugIds: a.eliminatedDrugIds,
            logEntries: a.logEntries,
          },
          eligibleDrugIds,
          currentNodeId,
          new Date().toISOString(),
        );
        setEligibleDrugIds(stepResult.remainingDrugIds);
        setEliminationLog((prev) => [...prev, ...stepResult.logEntries]);
        setAudit((prev) => ({
          ...prev,
          eliminationLog: [...(prev.eliminationLog ?? []), ...stepResult.logEntries],
        }));
        if (a.managementPlan) {
          setManagementPlanCaptures((prev) => [
            ...prev,
            {
              nodeId: currentNodeId,
              planText: a.managementPlan!.planText,
              patientAcknowledged: a.managementPlan!.patientAcknowledged,
              capturedAt: new Date().toISOString(),
            },
          ]);
          setAudit((prev) => ({
            ...prev,
            managementPlanCaptures: [
              ...(prev.managementPlanCaptures ?? []),
              {
                nodeId: currentNodeId,
                planText: a.managementPlan!.planText,
                patientAcknowledged: a.managementPlan!.patientAcknowledged,
                capturedAt: new Date().toISOString(),
              },
            ],
          }));
        }
        if (n.capturePreference && typeof a.answer === "boolean") {
          capturePreference(currentNodeId, n.text, a.answer ? "Yes" : "No");
        }
        // Elimination questions are still questions: a node declaring storesTo
        // must publish its answer to context like yes_no does, or downstream
        // condition routes and PGD pre-supply auto-answers never see it.
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo && typeof a.answer === "boolean") {
          const val = a.answer ? "yes" : "no";
          setContextValues((p) => ({ ...p, [n.storesTo]: val }));
          pendingCtx = { [n.storesTo]: val };
        }
        pendingChainCtx = pendingCtx;
        nextNodeId = getRoutingTarget(stepResult.nextTarget);
        if (nextNodeId) {
          advanceTo(nextNodeId, undefined, pendingCtx, stepResult.remainingDrugIds);
          return;
        }
        break;
      }

      case "drug_resolution": {
        const n = currentNode as any;
        const a = answer as DrugResolutionAnswer;
        setResolvedDrugId(a.resolvedDrugId);
        if (n.storesTo && a.selectionReason) {
          setContextValues((p) => ({ ...p, [n.storesTo]: a.selectionReason }));
        }
        setAudit((prev) => ({
          ...prev,
          drugResolution: {
            nodeId: currentNodeId,
            resolvedDrugId: a.resolvedDrugId,
            rankedEligibleDrugs: a.rankedEligibleDrugs,
            selectionReason: a.selectionReason,
            resolvedAt: new Date().toISOString(),
          },
          finalEligibleSet: eligibleDrugIds,
          overrides: a.overrideReason
            ? [
                ...(prev.overrides ?? []),
                {
                  id: `OVERRIDE-${Date.now()}`,
                  caution: a.resolvedDrugId,
                  decision: "selected_despite_avoid_tag",
                  // Clinician identity is not available to the engine; the
                  // consultation record carries the supplying pharmacist.
                  clinicianId: "unknown",
                  clinicianName: "Unknown",
                  reason: a.overrideReason,
                  timestamp: new Date().toISOString(),
                  nodeId: currentNodeId,
                },
              ]
            : prev.overrides,
        }));
        if (a.notSuitable && n.notSuitableOption) {
          nextNodeId = getRoutingTarget(n.notSuitableOption.next);
        } else if (eligibleDrugIds.length === 0) {
          nextNodeId = getRoutingTarget(n.noDrugsRemaining);
        } else if (eligibleDrugIds.length === 1) {
          nextNodeId = getRoutingTarget(n.singleDrugNext);
        } else {
          nextNodeId = getRoutingTarget(n.multipleDrugsNext);
        }
        break;
      }

      case "numeric_input": {
        const n = currentNode as any;
        const a = answer as NumericInputAnswer;
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: a.value }));
          pendingCtx = { [n.storesTo]: a.value };
        }
        if (n.linkedVitalType && onSaveVitals) {
          const ts = new Date().toISOString();
          if (n.linkedVitalType === "blood-pressure") {
            const systolic = contextValues["bpSystolic"];
            if (systolic != null) {
              onSaveVitals({ type: "blood-pressure", data: { systolic: String(systolic), diastolic: String(a.value) }, timestamp: ts });
            }
          } else {
            onSaveVitals({ type: n.linkedVitalType, data: { value: String(a.value), unit: n.unit }, timestamp: ts });
          }
        }
        if (a.plausibilityConfirmation) {
          setAudit((prev) => ({
            ...prev,
            plausibilityConfirmations: [
              ...(prev.plausibilityConfirmations ?? []),
              {
                nodeId: currentNodeId,
                value: a.value,
                message: a.plausibilityConfirmation!.message,
                confirmedByPharmacist: true,
                confirmedAt: new Date().toISOString(),
              },
            ],
          }));
        }
        nextNodeId = n.next;
        pendingChainCtx = pendingCtx;
        if (nextNodeId && pendingCtx && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "date_input":
      case "free_text": {
        const n = currentNode as any;
        const stored =
          currentNode.type === "date_input"
            ? (answer as DateInputAnswer).iso
            : (answer as FreeTextAnswer).text;
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: stored }));
          pendingCtx = { [n.storesTo]: stored };
        }
        nextNodeId = getRoutingTarget(n.next);
        pendingChainCtx = pendingCtx;
        if (nextNodeId && pendingCtx && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "clinician_assessment": {
        const n = currentNode as any;
        const a = answer as ClinicianAssessmentAnswer;
        setAudit((prev) => ({
          ...prev,
          assessmentRecords: [
            ...(prev.assessmentRecords ?? []),
            {
              nodeId: currentNodeId,
              notes: a.notes,
              conclusion: a.conclusion,
              conclusionLabel: a.conclusionLabel,
              recordedAt: new Date().toISOString(),
            },
          ],
        }));
        const target = a.conclusion === "optionA" ? n.conclusions.optionA.next : n.conclusions.optionB.next;
        nextNodeId = getRoutingTarget(target);
        break;
      }

      case "attestation": {
        const n = currentNode as any;
        const a = answer as AttestationAnswer;
        setAudit((prev) => ({
          ...prev,
          attestationRecords: [
            ...(prev.attestationRecords ?? []),
            {
              nodeId: currentNodeId,
              attested: a.attested,
              notes: a.notes,
              conclusion: a.conclusion,
              conclusionLabel: a.conclusionLabel,
              attestedAt: new Date().toISOString(),
            },
          ],
        }));
        const target = a.conclusion === "optionA" ? n.conclusions.optionA.next : n.conclusions.optionB.next;
        nextNodeId = getRoutingTarget(target);
        break;
      }

      case "iotf_lookup": {
        const n = currentNode as any;
        const a = answer as IOTFLookupAnswer;
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: a.result }));
          pendingCtx = { [n.storesTo]: a.result };
        }
        // Routing lives in one field per result; an iotf_lookup node has no `next`.
        switch (a.result) {
          case "meets_threshold":
            nextNodeId = getRoutingTarget(n.meetsThreshold);
            break;
          case "below_threshold":
            nextNodeId = getRoutingTarget(n.belowThreshold);
            break;
          case "pending_data":
            nextNodeId = getRoutingTarget(n.pendingDataOutcome);
            break;
          case "sex_not_resolvable":
          case "no_entry_found":
            nextNodeId = getRoutingTarget(n.sexNotResolvable);
            break;
        }
        pendingChainCtx = pendingCtx;
        if (nextNodeId && pendingCtx && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "confirmation_card": {
        const n = currentNode as any;
        const a = answer as ConfirmationCardResult;
        if (a.amendments.length > 0) {
          setAudit((prev) => ({
            ...prev,
            confirmationCardAmendments: [
              ...(prev.confirmationCardAmendments ?? []),
              ...a.amendments,
            ],
          }));
        }
        if (a.cannotProceedOutcomeId) {
          nextNodeId = getRoutingTarget(a.cannotProceedOutcomeId);
        } else {
          nextNodeId = n.next;
        }
        break;
      }

      case "bmi_capture": {
        const n = currentNode as any;
        const a = answer as BMICaptureAnswer;
        const pendingCtx: Record<string, unknown> = {};
        if (n.storesToHeight) {
          setContextValues((p) => ({ ...p, [n.storesToHeight]: a.heightCm }));
          pendingCtx[n.storesToHeight] = a.heightCm;
        }
        if (n.storesToWeight) {
          setContextValues((p) => ({ ...p, [n.storesToWeight]: a.weightKg }));
          setCapturedWeightKg(a.weightKg);
          pendingCtx[n.storesToWeight] = a.weightKg;
        }
        if (n.storesToBmi) {
          setContextValues((p) => ({ ...p, [n.storesToBmi]: a.bmi }));
          pendingCtx[n.storesToBmi] = a.bmi;
        }
        if (a.measurementSource) {
          setContextValues((p) => ({ ...p, bmiMeasurementSource: a.measurementSource }));
          pendingCtx.bmiMeasurementSource = a.measurementSource;
        }
        inlineVitals.save(currentNodeId, currentNode.type, {
          type: "bmi",
          data: {
            value: String(a.bmi),
            unit: "kg/m²",
            heightCm: a.heightCm,
            weightKg: a.weightKg,
            ...(a.measurementSource ? { measurementSource: a.measurementSource } : {}),
          },
          timestamp: new Date().toISOString(),
        });
        nextNodeId = getRoutingTarget(n.next);
        pendingChainCtx = pendingCtx;
        if (nextNodeId && !pathway.outcomes[nextNodeId]) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "gateway_alarm":
      case "gateway_progression": {
        const n = currentNode as any;
        // Gateway passage is recorded by the dedicated useEffect on currentNodeId —
        // do NOT call recordGateway here again or it double-records.
        if (currentNode.type === "gateway_alarm" && n.action === "close") {
          // Close the consultation immediately — record an emergency outcome
          // so the summary / completion modal can describe what happened, then
          // surface the summary for review.
          setAudit((prev) =>
            recordOutcome(
              prev,
              currentNodeId,
              "emergency",
              n.body,
              n.heading,
            ),
          );
          setEngineState("summary");
          return;
        }
        nextNodeId =
          currentNode.type === "gateway_progression" ? n.next : null;
        break;
      }

      case "outcome": {
        setAudit((prev) => {
          let updated = recordOutcome(
            prev,
            currentNodeId,
            outcomeNode?.outcomeType || "refer_routine",
            (outcomeNode as any)?.reason,
            (outcomeNode as any)?.title,
            (outcomeNode as any)?.safetyNetting,
          );
          // Record supply details immediately when outcome is supply
          if (outcomeNode?.outcomeType === "supply" && selectedPgdId) {
            try {
              const pgdForSupply = getPgd(selectedPgdId);
              const band = resolveDoseBand(pgdForSupply, effectivePatient, activeDoseStepId);
              if (band) {
                const supplyDetails = buildSupplyDetailsFromPgd(
                  selectedPgdId,
                  effectivePatient,
                  undefined,
                  false,
                  activeDoseStepId,
                );
                updated = recordSupplyDetails(updated, supplyDetails);
              } else {
                setErrorMessage(
                  "Supply details could not be recorded — patient weight may not be captured. Please review the prescription manually.",
                );
              }
            } catch (e) {
              console.error("Failed to build supply details:", e);
              setErrorMessage(
                "Supply details could not be recorded. Please review the prescription manually.",
              );
            }
          }
          return updated;
        });
        if (outcomeNode?.outcomeType === "chain" && (outcomeNode as any).chainId) {
          const chainId = (outcomeNode as any).chainId;
          setAudit((prev) => {
            const updated = {
              ...prev,
              finalOutcome: prev.finalOutcome
                ? { ...prev.finalOutcome, chainId }
                : undefined,
              pathwaySections: [
                ...(prev.pathwaySections ?? []),
                {
                  pathwayId,
                  sectionLabel: pathway.name,
                  completedAt: new Date().toISOString(),
                },
              ],
            };
            setTimeout(() => {
              setEngineState("complete");
              onComplete({ ...updated, contextSnapshot: { ...contextValues, ...(pendingChainCtx ?? {}) } });
            }, 0);
            return updated;
          });
          return;
        } else if (outcomeNode?.outcomeType === "supply" && selectedPgdId) {
          setEngineState("prescribing");
        } else {
          setEngineState("summary");
        }
        return;
      }
    }

    if (nextNodeId) {
      const chainOutcome = pathway.outcomes[nextNodeId];
      if (chainOutcome?.outcomeType === "chain" && (chainOutcome as any).chainId) {
        const chainId = (chainOutcome as any).chainId;
        setAudit((prev) => {
          const withOutcome = recordOutcome(
            prev,
            nextNodeId,
            "chain",
            (chainOutcome as any)?.reason,
            (chainOutcome as any)?.title,
          );
          const updated = {
            ...withOutcome,
            finalOutcome: { ...withOutcome.finalOutcome!, chainId },
            pathwaySections: [
              ...(withOutcome.pathwaySections ?? []),
              {
                pathwayId,
                sectionLabel: pathway.name,
                completedAt: new Date().toISOString(),
              },
            ],
          };
          setTimeout(() => {
            setEngineState("complete");
            onComplete({ ...updated, contextSnapshot: { ...contextValues, ...(pendingChainCtx ?? {}) } });
          }, 0);
          return updated;
        });
        return;
      }
      advanceTo(nextNodeId);
    }
  };

  const handleDirectNavigate = useCallback(
    (nodeId: string) => {
      eliminationCheckpoints.current[currentNodeId] = eliminationLog.length;
      setHistory((h) => [...h, currentNodeId]);
      setCurrentNodeId(nodeId);
    },
    [currentNodeId, eliminationLog],
  );

  const handleMultiSelectPreview = useCallback(
    (inProgressSelections: string[]) => {
      if (!currentNode || currentNode.type !== "multi_select") {
        setPreviewEligibleDrugIds(null);
        return;
      }
      if (inProgressSelections.length === 0) {
        setPreviewEligibleDrugIds(null);
        return;
      }
      const n = currentNode as any;
      if (typeof n.routing !== "function") {
        setPreviewEligibleDrugIds(null);
        return;
      }

      // Union approach — evaluate eliminations for each individual selection
      // independently, then union them. This ensures combined selections always
      // show the most restrictive preview rather than a single first-match route.
      const drugsToEliminate = new Set<string>();

      for (const singleSelection of inProgressSelections) {
        const singleRoute = n.routing([singleSelection], effectivePatient);
        if (!singleRoute || typeof singleRoute !== "string") continue;

        // Follow condition_route hops (up to 5) to reach the elimination node,
        // using a preview context seeded with just this single selection.
        let targetNode: any = pathway?.nodes?.find((nd: any) => nd.id === singleRoute);
        let hops = 0;
        while (targetNode && targetNode.type === "condition_route" && hops < 5) {
          const previewCtx = {
            context: {
              ...contextValues,
              ...(n.storesTo ? { [n.storesTo]: [singleSelection] } : {}),
            },
            eligibleDrugIds,
            patient: effectivePatient,
          };
          let followed = false;
          for (const condition of targetNode.conditions ?? []) {
            let matches = false;
            try { matches = condition.evaluate(previewCtx); } catch { matches = false; }
            if (matches) {
              const nextId = typeof condition.then === "string" ? condition.then : null;
              if (nextId) {
                targetNode = pathway?.nodes?.find((nd: any) => nd.id === nextId);
                followed = true;
              }
              break;
            }
          }
          if (!followed) break;
          hops++;
        }

        if (!targetNode || targetNode.type !== "drug_elimination" || !targetNode.autoAdvance) continue;

        for (const rule of targetNode.eliminateOnYes ?? []) {
          if (!rule.trigger || rule.trigger.kind === "always") {
            for (const drugId of rule.drugIds ?? []) drugsToEliminate.add(drugId);
          }
        }
      }

      if (drugsToEliminate.size === 0) {
        setPreviewEligibleDrugIds(null);
        return;
      }
      setPreviewEligibleDrugIds(eligibleDrugIds.filter((id) => !drugsToEliminate.has(id)));
    },
    [currentNode, effectivePatient, pathway, eligibleDrugIds, contextValues],
  );

  const calculateAgeYearsFromDateOfBirth = (dateOfBirth: unknown): number | null => {
    if (typeof dateOfBirth !== "string" || !dateOfBirth.trim()) return null;
    const trimmed = dateOfBirth.trim();
    const ukMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    const dob = ukMatch
      ? new Date(Number(ukMatch[3]), Number(ukMatch[2]) - 1, Number(ukMatch[1]))
      : new Date(trimmed);
    if (Number.isNaN(dob.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return Math.max(0, age);
  };

  const handleBannerConfirm = (
    amendments: Amendment[],
    extras: { consentObtained: boolean },
  ) => {
    if (!pathway) {
      console.error("Pathway not loaded");
      return;
    }


    const confirmedValues: Record<string, unknown> = {};
    pathway.demographicConfiguration.bannerFields.forEach((field) => {
      const amendment = amendments.find((a) => a.field === field.field);
      confirmedValues[field.field] = amendment ? amendment.newValue : (patient as any)[field.field];
    });
    Object.assign(confirmedValues, withDerivedAge(pathway, confirmedValues));

    // Check demographic eligibility rules if defined
    if (pathway.demographicEligibility) {
      const elig = pathway.demographicEligibility;

      // Sex check
      if (elig.requiresSex && elig.requiresSex !== "any") {
        const isFemale = confirmedValues.isFemale;
        if (
          (elig.requiresSex === "female" && isFemale === false) ||
          (elig.requiresSex === "male" && isFemale === true)
        ) {
          setDemographicBlockMessage(
            elig.blockMessage || "This patient does not meet the sex requirement for this pathway.",
          );
          return;
        }
      }

      // Age check
      const ageYears = calculateAgeYearsFromDateOfBirth(confirmedValues.dateOfBirth);
      if (ageYears !== null) {
        if (elig.minAge !== undefined && ageYears < elig.minAge) {
          setDemographicBlockMessage(
            elig.blockMessage || `This pathway requires a minimum age of ${elig.minAge} years.`,
          );
          return;
        }
        if (elig.maxAge !== undefined && ageYears >= elig.maxAge) {
          setDemographicBlockMessage(
            elig.blockMessage || `This pathway requires an age under ${elig.maxAge} years.`,
          );
          return;
        }
      }
    }

    setDemographicBlockMessage(null);
    setAudit((prev) =>
      recordBannerConfirmation(prev, confirmedValues, amendments, {
        consentObtained: extras.consentObtained,
      }),
    );

    const demographicFields = new Set(["dateOfBirth", "biologicalSex", "isFemale", "ethnicAdjustmentGroup"]);
    const demographicAmendments = amendments.filter((a) => demographicFields.has(a.field));
    if (demographicAmendments.length > 0 && onDemographicAmended) {
      onDemographicAmended(demographicAmendments);
    }

    setContextValues((p) => ({
      ...p,
      consentObtained: extras.consentObtained,
    }));
    setEngineState("consulting");
  };

  if (engineState === "banner") {
    // Check eligibility on initial render
    if (!demographicBlockMessage && pathway?.demographicEligibility) {
      const elig = pathway.demographicEligibility;
      const ageYears = calculateAgeYearsFromDateOfBirth(patient.dateOfBirth);

      // Sex check
      if (elig.requiresSex && elig.requiresSex !== "any") {
        if (
          (elig.requiresSex === "female" && patient.isFemale === false) ||
          (elig.requiresSex === "male" && patient.isFemale === true)
        ) {
          setDemographicBlockMessage(
            elig.blockMessage || "This patient does not meet the sex requirement for this pathway.",
          );
        }
      }

      // Age check
      if (!demographicBlockMessage && ageYears !== null) {
        if (elig.minAge !== undefined && ageYears < elig.minAge) {
          setDemographicBlockMessage(
            elig.blockMessage || `This pathway requires a minimum age of ${elig.minAge} years.`,
          );
        } else if (elig.maxAge !== undefined && ageYears >= elig.maxAge) {
          setDemographicBlockMessage(
            elig.blockMessage || `This pathway requires an age under ${elig.maxAge} years.`,
          );
        }
      }
    }

    return (
      <div
        className={`flex flex-col h-full ${isDarkMode ? "bg-gray-900" : "bg-gray-50"
          }`}
      >
        <div
          className={`border-b ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
            } px-4 pt-2 pb-3`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p
                className={`text-[10px] uppercase tracking-wide font-semibold ${isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
              >
                Pathway
              </p>
              <h2
                className={`text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                title={pathway.name}
              >
                {pathway.name}
              </h2>
              {headerLinks.length > 0 && (
                <Collapsible open={clinicalRefsOpen} onOpenChange={setClinicalRefsOpen} className="mt-1.5">
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      title={clinicalRefsOpen ? "Collapse clinical references" : "Expand clinical references"}
                      className="text-xs h-auto py-1"
                    >
                      <Info className="h-3 w-3" />
                      <span>Clinical References ({headerLinks.length})</span>
                      <ChevronDown className={`h-3 w-3 transition-transform ${clinicalRefsOpen ? "rotate-180" : ""}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <div className="flex flex-wrap gap-1.5">
                      {headerLinks.map((l: any, idx: number) => (
                        <a
                          key={idx}
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Open ${l.label} in a new tab`}
                          style={{ animation: clinicalRefsOpen ? `fadeInSlideUp 300ms ease-out ${idx * 10}ms both` : 'none' }}
                          className={`group inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${isDarkMode ? "border-gray-700 bg-gray-900 text-blue-300 hover:bg-gray-800 hover:border-blue-600" : "border-gray-200 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-300"}`}
                        >
                          <Info className={`h-3 w-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                          <span className="underline underline-offset-2 decoration-dotted">{l.label}</span>
                          <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                        </a>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {demographicBlockMessage && (
            <OutcomeNode
              node={{
                id: "OUT_INELIGIBLE",
                type: "outcome",
                outcomeType: "refer_routine",
                title: "Refer to prescriber",
                reason: demographicBlockMessage,
                safetyNetting: ["Patient is not eligible for this pathway. Refer to a prescriber for assessment and treatment."],
              } as any}
              isDarkMode={isDarkMode}
              pathway={pathway}
              onClose={() => {
                setAudit((prev) =>
                  recordOutcome(
                    prev,
                    "OUT_INELIGIBLE",
                    "refer_routine",
                    demographicBlockMessage,
                    "Refer to prescriber",
                    ["Patient is not eligible for this pathway. Refer to a prescriber for assessment and treatment."]
                  )
                );
                setEngineState("summary");
              }}
            />
          )}
          {!demographicBlockMessage && (
            <DemographicConfirmationBanner
              configuration={pathway.demographicConfiguration}
              patient={patient}
              isDarkMode={isDarkMode}
              onConfirm={handleBannerConfirm}
              onCancel={onCancel}
              onConsentRefused={onConsentRefused}
              refined={!!pathway.refinedFlowUi}
            />
          )}
        </div>
      </div>
    );
  }

  // Lookup PGD-derived counselling/cautions for supply outcomes (used in
  // both the prescribing and summary render paths below).
  let outcomeCounselling: string[] | undefined;
  let outcomeCautions: any[] | undefined;
  if (
    isOutcome &&
    outcomeNode?.outcomeType === "supply" &&
    selectedPgdId
  ) {
    try {
      const pgd = getPgd(selectedPgdId);
      outcomeCounselling = pgd.counselling;
      outcomeCautions = pgd.cautions;
    } catch {
      // ignore
    }
  }

  if (engineState === "prescribing" && selectedPgdId && outcomeNode?.outcomeType === "supply") {
    let defaultCategory: "pgd" | "pharmacy-first" = "pgd";
    try {
      const pgd = getPgd(selectedPgdId);
      if (pgd.frameworkType === "pharmacy_first") {
        defaultCategory = "pharmacy-first";
      }
    } catch {
      // fall back to "pgd"
    }
    return (
      <>
      <div className={`flex-1 overflow-y-auto p-6 space-y-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
        <div
          className={`border rounded-2xl p-6 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
            }`}
        >
          <h2
            className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"
              }`}
          >
            Generate PGD Supply
          </h2>
          {headerLinks.length > 0 && (
            <div className="mt-1.5 mb-2 flex flex-wrap gap-1.5">
              {headerLinks.map((l: any, idx: number) => (
                <a
                  key={idx}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open ${l.label} in a new tab`}
                  className={`group inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${isDarkMode ? "border-gray-700 bg-gray-900 text-blue-300 hover:bg-gray-800 hover:border-blue-600" : "border-gray-200 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-300"}`}
                >
                  <Info className={`h-3 w-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  <span className="underline underline-offset-2 decoration-dotted">{l.label}</span>
                  <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                </a>
              ))}
            </div>
          )}
          <p
            className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
          >
            The PGD-permitted medication, dose and quantity have been pre-filled
            from {selectedPgdId}. You can edit any field (e.g. swap formulation
            based on stock) before issuing.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setEngineState("summary")}
            >
              Skip
            </Button>
            <PrescriptionModal
              trigger={
                <Button size="lg" className="flex-1">
                  Issue PGD Supply
                </Button>
              }
              isDarkMode={isDarkMode}
              prefillItems={prefillItems}
              allowedMedications={allowedMedications}
              defaultCategory={defaultCategory}
              lockedMedications={false}
              onSave={(prescription) => {
                const items: any[] = prescription?.items || [];
                const firstItem = items.find((item: any) => item?.value);
                // The modal returns the medication label, not a registry drug
                // id, so only record a swap that resolves to a registered
                // formulation — otherwise supplyDetails.drugId stops naming a
                // drug and the summary cannot show the product.
                const issued =
                  firstItem && selectedPgdId
                    ? matchFormulationFromPrescription(selectedPgdId, firstItem)
                    : null;
                if (issued) {
                  setAudit((prev) => {
                    if (!prev.supplyDetails) return prev;
                    // Issuing a different strength changes the dose band, so
                    // dose, instruction and quantity are re-read from that
                    // band — patching the ids alone leaves the audit and the
                    // summary describing the strength that was prefilled.
                    try {
                      return recordSupplyDetails(
                        prev,
                        buildSupplyDetailsFromPgd(
                          prev.supplyDetails.pgdId,
                          effectivePatient,
                          issued.formulationId,
                          prev.supplyDetails.sugarFreeSelected,
                          issued.doseStepId ?? activeDoseStepId,
                        ),
                      );
                    } catch {
                      return {
                        ...prev,
                        supplyDetails: {
                          ...prev.supplyDetails,
                          drugId: issued.drugId,
                          formulationId: issued.formulationId,
                        },
                      };
                    }
                  });
                }
                const months = prescription?.monthsSupplied ?? 1;
                setMonthsSupplied(months);
                if (months > 1 && prescription?.extendedSupplyReason) {
                  setAudit((prev) =>
                    recordExtendedSupply(
                      prev,
                      months,
                      prescription.extendedSupplyReason,
                      new Date().toISOString()
                    )
                  );
                }
                if (months > 1) {
                  setShowAhtAppointmentAdjustModal(true);
                  return;
                }
                setEngineState("summary");
              }}
            />
          </div>
        </div>
      </div>
      <AHTAppointmentReviewModal
        open={showAhtAppointmentAdjustModal}
        appointments={ahtPlannedAppointments}
        monthsSupplied={monthsSupplied}
        isDarkMode={isDarkMode}
        onCancel={async () => {
          setShowAhtAppointmentAdjustModal(false);
          if (!ahtTreatmentPlanCreated) {
            try {
              await buildAndSavePlan(ahtPlannedAppointments);
            } catch (e) {
              console.error("Failed to create AHT treatment plan:", e);
            }
          }
          setEngineState("summary");
        }}
        onSave={async (updatedAppointments) => {
          setAhtPlannedAppointments(updatedAppointments);
          // Only close once the plan is actually saved, so a failed save leaves
          // the modal open to retry instead of silently losing the plan.
          try {
            await buildAndSavePlan(updatedAppointments);
            setShowAhtAppointmentAdjustModal(false);
          } catch (e) {
            console.error("Failed to create AHT treatment plan:", e);
          }
        }}
      />
      </>
    );
  }

  if (engineState === "summary") {
    const chainStartedAt = contextValues._chainStartedAt as string | undefined;
    const chainVisibleCount = (contextValues._chainVisibleAnswerCount as number | undefined) ?? 0;
    return (
      <ConsultationSummary
        audit={audit}
        pathway={pathway}
        isDarkMode={isDarkMode}
        overrideStartedAt={chainStartedAt}
        overrideTotalAnswers={chainVisibleCount > 0 ? chainVisibleCount + answeredVisibleCount : answeredVisibleCount}
        onClose={() => {
          setEngineState("complete");
          onComplete({ ...audit, contextSnapshot: { ...contextValues, _visibleAnswerCount: answeredVisibleCount } });
        }}
      />
    );
  }

  if (engineState === "complete") {
    return null;
  }

  if (!currentNode) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Current node not found: {currentNodeId}</AlertDescription>
        </Alert>
      </div>
    );
  }


  return (
    <FlowFooterProvider><div
      className={`flex flex-col h-full ${isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
    >
      <div
        className={`border-b ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
          } px-4 pt-2 pb-3`}
      >
        <div className="relative flex items-center justify-between mb-1.5">
          <div className="min-w-0">
            <p
              className={`text-[10px] uppercase tracking-wide font-semibold ${isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
            >
              Pathway
            </p>
            <h2
              className={`text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"
                }`}
              title={pathway.name}
            >
              {pathway.name}
            </h2>
            {headerLinks.length > 0 && (
              <Collapsible open={clinicalRefsOpen} onOpenChange={setClinicalRefsOpen} className="mt-1.5">
                <CollapsibleTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    title={clinicalRefsOpen ? "Collapse clinical references" : "Expand clinical references"}
                    className="text-xs h-auto py-1"
                  >
                    <Info className="h-3 w-3" />
                    <span>Clinical References ({headerLinks.length})</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${clinicalRefsOpen ? "rotate-180" : ""}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <div className="flex flex-wrap gap-1.5">
                    {headerLinks.map((l: any, idx: number) => (
                      <a
                        key={idx}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${l.label} in a new tab`}
                        style={{ animation: clinicalRefsOpen ? `fadeInSlideUp 300ms ease-out ${idx * 10}ms both` : 'none' }}
                        className={`group inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${isDarkMode ? "border-gray-700 bg-gray-900 text-blue-300 hover:bg-gray-800 hover:border-blue-600" : "border-gray-200 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-300"}`}
                      >
                        <Info className={`h-3 w-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                        <span className="underline underline-offset-2 decoration-dotted">{l.label}</span>
                        <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                      </a>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className={`absolute right-0 top-0 text-xs underline-offset-2 hover:underline whitespace-nowrap ${isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
            >
              Change pathway
            </button>
          )}
        </div>
        <PathwayProgressHeader
          pathway={pathway}
          answeredVisibleCount={answeredVisibleCount}
          answeredNodesCount={answeredNodesCount}
          progressPercent={progressPercent}
          paymentTriggered={!!audit.paymentTriggered}
          isDarkMode={isDarkMode}
        />
      </div>

      {errorMessage && (
        <div className="p-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        </div>
      )}

      <div
        ref={contentScrollRef}
        className={`flex-1 overflow-y-auto p-6 space-y-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        {showEmptyDrugSetAlert && (
          <Alert className="mb-4 border-red-300 bg-red-50 text-red-900">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription>
              No eligible treatments available under this service. Refer patient to GP.
            </AlertDescription>
          </Alert>
        )}

        {currentNode.type === "yes_no" && !evalAutoAnswer((currentNode as any).autoAnswer, effectivePatient) && (
          <YesNoNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as "yes" | "no" | null) || null}
            stepNumber={answeredNodesCount + 1}
          />
        )}

        {currentNode.type === "checklist" && (
          <ChecklistNode
            key={currentNodeId}
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as string[] | undefined) ?? null}
            stepNumber={answeredNodesCount + 1}
            patient={patient}
            biologicalSex={confirmedBiologicalSex(effectivePatient as any, contextValues)}
          />
        )}

        {currentNode.type === "score" && (
          <ScoreNode
            key={currentNodeId}
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as any) || null}
            stepNumber={answeredNodesCount + 1}
            onOpenCalculator={onOpenCalculator}
          />
        )}

        {currentNode.type === "single_select" && (
          <SingleSelectNode
            key={currentNodeId}
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as string) || null}
            stepNumber={answeredNodesCount + 1}
            preface={nodePrefaces?.[currentNode.id]}
          />
        )}

        {currentNode.type === "multi_select" && (
          <MultiSelectNode
            key={currentNodeId}
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            onSelectionChange={handleMultiSelectPreview}
            value={(answers.get(currentNodeId) as string[] | undefined) ?? null}
            stepNumber={answeredNodesCount + 1}
            patient={patient}
          />
        )}

        {currentNode.type === "severity" && (
          <SeverityNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as "mild" | "moderate" | "severe" | null) || null}
            stepNumber={answeredNodesCount + 1}
          />
        )}

        {currentNode.type === "gateway_alarm" && (
          <GatewayAlarmNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            pathway={pathway}
            onAcknowledge={(action) => {
              if (action) setAudit((p) => recordAnswer(p, "alarm-action-taken", "single_select", action));
              handleAnswer(true);
            }}
            onOpenCalculator={onOpenCalculator}
            onBack={handleStepBack}
            refined={!!pathway.refinedFlowUi}
            {...alarmFlagContext(pathway, (currentNode as any).flagActions?.sourceNodeId, answers)}
          />
        )}

        {currentNode.type === "gateway_progression" && (() => {
          const n = currentNode as any;
          if (n.id === "Q12_blood_pressure_controlled") {
            return (
              <InlineBPCaptureNode
                key={currentNode.id}
                isDarkMode={isDarkMode}
                patientAgeYears={(effectivePatient as any).ageYears}
                {...inlineVitals.props(n)}
                onAnswer={(answer) => {
                  const nextNodeId =
                    answer.conclusion === "optionA"
                      ? "Q13_active_suicidal_ideation"
                      : "Q12_bp_mysimba_elimination";
                  handleDirectNavigate(nextNodeId);
                }}
                stepNumber={answeredNodesCount + 1}
                existingSystolic={(() => {
                  const v = patientVitals?.bloodPressure?.systolic?.value;
                  if (v == null) return null;
                  const n = typeof v === "string" ? parseFloat(v) : v;
                  return Number.isFinite(n) && n > 0 ? n : null;
                })()}
                existingDiastolic={(() => {
                  const v = patientVitals?.bloodPressure?.diastolic?.value;
                  if (v == null) return null;
                  const n = typeof v === "string" ? parseFloat(v) : v;
                  return Number.isFinite(n) && n > 0 ? n : null;
                })()}
              />
            );
          }
          const ahtPat = effectivePatient as any;
          const patAgeMonths: number | undefined = ahtPat.ageMonths;
          const isAdolescentBmiNode =
            typeof patAgeMonths === "number" &&
            patAgeMonths >= 144 &&
            patAgeMonths < 216;
          const iotfTableForNode = isAdolescentBmiNode
            ? (() => { try { return getIOTFTable("iotf-cutoffs-v1"); } catch { return undefined; } })()
            : undefined;
          return (
            <GatewayProgressionNode
              node={n}
              isDarkMode={isDarkMode}
              onContinue={() => handleAnswer(true)}
              iotfTable={iotfTableForNode}
              ageMonths={isAdolescentBmiNode ? patAgeMonths : undefined}
              biologicalSex={isAdolescentBmiNode ? ahtPat.biologicalSex?.value : undefined}
            />
          );
        })()}

        {currentNode.type === "bmi_capture" && (
          <BMICaptureNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as any) || null}
            stepNumber={answeredNodesCount + 1}
            patientEthnicAdjustmentGroup={(() => {
              const raw = (effectivePatient as any)?.ethnicAdjustmentGroup;
              const val = typeof raw === "object" && raw !== null ? raw.value : raw;
              return val;
            })()}
            showInclusionResult={
              ((effectivePatient as any).ageYears ?? 0) >= 18 &&
              contextValues["aht-sel-consultation-type"] !== "reinitiation"
            }
            lastRecorded={(() => {
              const num = (v: unknown) => {
                if (v == null) return undefined;
                const n = typeof v === "string" ? parseFloat(v) : (v as number);
                return Number.isFinite(n) && n > 0 ? n : undefined;
              };
              const heightCm = num(patientVitals?.height?.value);
              const weightKg = num(patientVitals?.weight?.value);
              const recordedAt =
                patientVitals?.weight?.recordedAt ?? patientVitals?.height?.recordedAt ?? undefined;
              return { heightCm, weightKg, recordedAt };
            })()}
          />
        )}

        {currentNode.type === "weight_capture" && (
          <WeightCaptureNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(w) => handleAnswer(w)}
            value={(answers.get(currentNodeId) as number) ?? capturedWeightKg ?? null}
            stepNumber={answeredNodesCount + 1}
            pgd={selectedPgdId ? (() => { try { return getPgd(selectedPgdId); } catch { return undefined; } })() : undefined}
          />
        )}

        {currentNode.type === "outcome" && outcomeNode && (
          <OutcomeNode
            node={outcomeNode as any}
            isDarkMode={isDarkMode}
            reasonList={resolveOutcomeReasonList(outcomeNode as any, contextValues)}
            counsellingPoints={outcomeCounselling}
            cautions={outcomeCautions}
            pathway={pathway}
            supplyDetails={audit.supplyDetails}
            drugId={(outcomeNode as any)?.drugRef || audit.supplyDetails?.drugId}
            onClose={() => handleAnswer(true)}
            onOpenCalculator={onOpenCalculator}
            onCreateTreatmentPlan={
              pathway.id.startsWith("aht-") && (outcomeNode as any)?.outcomeType === "supply"
                ? handleCreateAHTTreatmentPlan
                : undefined
            }
            treatmentPlanExists={ahtTreatmentPlanCreated}
            requireAcknowledgements={pathway.id.startsWith("aht-")}
            onAcknowledge={(key) =>
              setAudit((prev) => recordSupplyAcknowledgement(prev, key, new Date().toISOString()))
            }
          />
        )}

        {showAHTTreatmentPlanModal &&
          audit.supplyDetails &&
          ahtPlannedAppointments.length > 0 && (() => {
            const drugId = audit.supplyDetails!.drugId as AHTDrugId;
            const pgdId = audit.supplyDetails!.pgdId ?? selectedPgdId ?? "";
            let pgd: ReturnType<typeof getPgd> | undefined;
            try { pgd = getPgd(pgdId); } catch { /* ignore */ }
            const reviewSchedule = pgd?.reviewSchedule;
            if (!reviewSchedule) return null;

            const weightKg =
              typeof contextValues.weightKg === "number" ? contextValues.weightKg : 0;
            const bmi =
              typeof contextValues.bmi === "number" ? contextValues.bmi : 0;

            return (
              <AHTTreatmentPlanConfirmModal
                open={showAHTTreatmentPlanModal}
                isDarkMode={isDarkMode}
                patientName={patientDisplayName ?? patient.patientId}
                patientId={patient.patientId}
                patientEmail={patientEmail ?? null}
                onUpdatePatientEmail={onUpdatePatientEmail}
                episodeOrigin="initialSupply"
                drugName={drugId}
                pgdId={pgdId}
                initialDose={audit.supplyDetails!.instructionText}
                baselineWeightKg={weightKg}
                baselineBmi={bmi}
                plannedAppointments={ahtPlannedAppointments}
                onCancel={() => setShowAHTTreatmentPlanModal(false)}
                onConfirm={async () => {
                  if (monthsSupplied > 1) {
                    setShowAHTTreatmentPlanModal(false);
                    setShowAhtAppointmentAdjustModal(true);
                    return;
                  }
                  await buildAndSavePlan(ahtPlannedAppointments);
                }}
              />
            );
          })()}

        <AHTPatientEmailModal
          open={showAHTPatientEmailModal}
          patientName={ahtEmailPlan ? ahtEmailPlan.patientName : ""}
          patientEmail={patientEmail ?? null}
          drugId={ahtEmailPlan ? ahtEmailPlan.resolvedDrugId : ""}
          drugDisplayName={ahtEmailPlan ? getAhtDrugDisplayName(ahtEmailPlan.resolvedDrugId) : ""}
          initialDose={ahtEmailPlan ? ahtEmailPlan.currentDose : ""}
          nextAppointmentDate={ahtEmailPlan?.plannedAppointmentDetails[0] ? ahtEmailPlan.plannedAppointmentDetails[0].date : null}
          isDarkMode={isDarkMode}
          onSend={() => {
            handleAhtPatientEmailSend();
            handleAnswer(true);
          }}
          onSkip={() => {
            handleAhtPatientEmailSkip();
            handleAnswer(true);
          }}
        />

        {currentNode.type === "drug_selection" &&
          isInteractiveDrugSelection(currentNode) && (
            <DrugSelectionNode
              node={currentNode as any}
              isDarkMode={isDarkMode}
              context={contextValues}
              pathwayPgdRefs={pathway.pgdRefs}
              stepNumber={answeredNodesCount + 1}
              onAccept={(rule, ruleIndex) =>
                handleAnswer({ pgdId: rule.pgdId, ruleIndex })
              }
              onOverride={(pgdId, overrideReason, recommendedPgdId) =>
                handleAnswer({
                  pgdId,
                  ruleIndex: -1,
                  overrideReason,
                  recommendedPgdId,
                })
              }
              onSelect={(pgdId) => handleAnswer({ pgdId, ruleIndex: 0 })}
            />
          )}

        {currentNode.type === "drug_elimination" && (currentNode as any).autoAdvance !== true && !evalAutoAnswer((currentNode as any).autoAnswer, effectivePatient) && (
          <DrugEliminationNode
            // Keyed per node: these components seed their selection state from
            // `value` on mount, so without a key React reuses one instance for
            // every node of the type and the seed never re-runs — the previous
            // answer is not shown on Back, and the next question inherits it.
            key={currentNodeId}
            node={currentNode as any}
            eligibleDrugIds={eligibleDrugIds}
            drugLabels={
              (pathway as any).drugLabels ??
              (Object.keys(drugLabels).length > 0
                ? drugLabels
                : Object.fromEntries(eligibleDrugIds.map((id) => {
                    try { return [id, getDrug(id).name]; } catch { return [id, id]; }
                  })))
            }
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            value={(answers.get(currentNodeId) as any) || null}
            onAnswer={(value) => handleAnswer(value)}
            onPreviewChange={setEliminationPreview}
            evaluationPatient={effectivePatient}
            evaluationContext={contextValues}
          />
        )}

        {currentNode.type === "drug_resolution" && (
          <DrugResolutionNode
            node={resolveDrugResolutionNode(currentNode as any, {
              patient: effectivePatient,
              context: contextValues,
              eligibleDrugIds,
            })}
            eligibleDrugIds={eligibleDrugIds}
            drugLabels={
              (pathway as any).drugLabels ??
              // Labels must cover the starting set too, so showEliminated rows
              // are not rendered as bare drug IDs.
              Object.fromEntries(
                Array.from(
                  new Set([...((pathway as any).initialEligibleDrugIds ?? []), ...eligibleDrugIds])
                ).map((id) => {
                  try { return [id, getDrug(id as string).name]; } catch { return [id, id]; }
                })
              )
            }
            accumulatedFlags={accumulatedFlags}
            auditFlagLabels={(pathway as any).auditFlagLabels}
            managementPlanCaptures={managementPlanCaptures}
            preferenceCaptures={preferenceCaptures}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            reinitiationDrugId={
              (contextValues["aht-sel-reinitiation-drug"] as string | undefined) ?? null
            }
            initialEligibleDrugIds={(pathway as any).initialEligibleDrugIds}
            eliminationLog={audit.eliminationLog}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "numeric_input" && (
          <NumericInputNode
            key={currentNodeId}
            node={currentNode as any}
            prePopulatedValue={
              // A previously given answer wins: stepping back clears the
              // context key this node wrote, so only `answers` still holds it.
              (answers.get(currentNodeId) as number | undefined) ??
              ((currentNode as any).storesTo
                ? (contextValues[(currentNode as any).storesTo] as number | undefined) ?? null
                : null)
            }
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "date_input" && (
          <DateInputNode
            key={currentNodeId}
            node={currentNode as any}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(a) => handleAnswer(a)}
          />
        )}

        {currentNode.type === "free_text" && (
          <FreeTextNode
            key={currentNodeId}
            node={currentNode as any}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(a) => handleAnswer(a)}
          />
        )}

        {currentNode.type === "clinician_assessment" && (
          <ClinicianAssessmentNode
            key={currentNodeId}
            node={currentNode as any}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            value={(answers.get(currentNodeId) as any) || null}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "attestation" && (
          <AttestationNode
            key={currentNodeId}
            node={currentNode as any}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            value={(answers.get(currentNodeId) as any) || null}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "iotf_lookup" && (
          <IOTFLookupNode
            node={currentNode as any}
            ageMonths={
              effectivePatient.ageYears !== undefined && effectivePatient.ageYears !== null
                ? Math.round(effectivePatient.ageYears * 12)
                : 0
            }
            biologicalSex={
              effectivePatient.isFemale === true
                ? "female"
                : effectivePatient.isFemale === false
                  ? "male"
                  : "other"
            }
            bmi={
              (contextValues.bmi as number) ??
              (typeof effectivePatient.weightKg === "number" &&
                typeof (effectivePatient as any).heightCm === "number" &&
                (effectivePatient as any).heightCm > 0
                ? (effectivePatient.weightKg / (((effectivePatient as any).heightCm / 100) ** 2))
                : 0)
            }
            iotfTable={(() => { try { return getIOTFTable((currentNode as any).iotfTableId); } catch { return { id: "", version: "", status: "pending_data" as const, source: "", validFrom: "", cutOffs: [] }; } })()}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "confirmation_card" && (
          <ConfirmationCardNode
            key={currentNodeId}
            node={currentNode as any}
            auditAnswers={[
              ...(Array.isArray(contextValues._chainAuditAnswers) ? contextValues._chainAuditAnswers as any[] : []),
              ...audit.answers,
            ]}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            value={(answers.get(currentNodeId) as any) || null}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "flag" && (() => {
          const flagNode = currentNode as any;
          return (
            <div className="space-y-4">
              <Alert className={
                flagNode.variant === "warn"
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : "border-blue-200 bg-blue-50 text-blue-900"
              }>
                <AlertCircle className={
                  flagNode.variant === "warn" ? "h-4 w-4 text-amber-600" : "h-4 w-4 text-blue-600"
                } />
                <AlertDescription className="text-sm leading-relaxed">
                  {(() => {
                    const lines = String(flagNode.message ?? "").split("\n").filter(Boolean);
                    const bullets = lines.filter((l) => l.startsWith("•"));
                    const headers = lines.filter((l) => !l.startsWith("•"));
                    return (
                      <>
                        {headers.map((line, i) => <p key={i} className="mb-1">{line}</p>)}
                        {bullets.length > 0 && (
                          <ul className="list-disc space-y-1 mb-2">
                            {bullets.map((line, i) => (
                              <li key={i} className="ml-4">{line.slice(1).trim()}</li>
                            ))}
                          </ul>
                        )}
                      </>
                    );
                  })()}
                </AlertDescription>
              </Alert>
              <button
                onClick={() => handleAnswer(null)}
                className="w-full py-3 px-4 rounded-lg font-medium bg-gray-900 text-white hover:bg-gray-700 active:bg-gray-800 transition-colors"
              >
                I have read and understood this
              </button>
            </div>
          );
        })()}

        {currentNode.type === "drug_selection" &&
          !isInteractiveDrugSelection(currentNode) && (
            <div className="text-center py-8">
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Processing...
              </p>
            </div>
          )}

        {currentNode.type === "pgd_exclusion_check" && ((currentNode as any).pgdIdOverride || selectedPgdId) && (() => {
          const n = currentNode as any;
          const pgdId = n.pgdIdOverride || selectedPgdId;
          let pgd;
          try {
            pgd = getPgd(pgdId);
          } catch {
            return (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Could not load PGD {pgdId}.
                </AlertDescription>
              </Alert>
            );
          }
          const checks = pgd.preSupplyChecks || [];

          if (n.variant !== "sequential") {
            const label = String(pgd.id).startsWith("protocol-") ? "Protocol" : "PGD";
            let drugName: string | null = null;
            try { if (pgd.drugRef) drugName = getDrug(pgd.drugRef).name; } catch { drugName = null; }
            const condition = (pgd.indication || "").split(/[—–-]/)[0].trim();
            const parts = [condition, drugName].filter(Boolean).join(" — ");
            const syntheticNode: ChecklistNodeShape = {
              id: currentNodeId,
              type: "checklist",
              text: `${label} pre-supply checks — ${parts} (${pgd.reference})`,
              items: checks.map((check) => ({
                id: check.id,
                text: check.question,
                hint: check.hint,
                linkRefs: check.linkRefs,
              })),
              includeNoneOption: true,
              anySelected: n.anyTriggered,
              noneSelected: n.allCleared,
            };
            return (
              <ChecklistNode
                key={currentNodeId}
                node={syntheticNode}
                isDarkMode={isDarkMode}
                onAnswer={(selectedIds) => handleAnswer(selectedIds)}
                value={(answers.get(currentNodeId) as string[] | undefined) ?? null}
                stepNumber={answeredNodesCount + 1}
                patient={patient}
              />
            );
          }

          // Hide checks that don't apply to this patient's age group, then split
          // into inclusion (must all be Yes) and exclusion (must all be No).
          const isAdult = ((effectivePatient as any)?.ageYears ?? 99) >= 18;
          const visibleChecks = checks.filter((check: any) => {
            if (check.patientGroup === "adult" && !isAdult) return false;
            if (check.patientGroup === "adolescent" && isAdult) return false;
            return true;
          });

          const inclusionChecks = visibleChecks.filter(
            (c: any) => c.direction === "exclude_on_no",
          );
          const exclusionChecks = visibleChecks.filter(
            (c: any) => c.direction !== "exclude_on_no",
          );
          const hasBothGroups =
            inclusionChecks.length > 0 && exclusionChecks.length > 0;

          const allAnswered = visibleChecks.every(
            (c) => exclusionAnswers[c.id] === "yes" ||
              exclusionAnswers[c.id] === "no",
          );
          const anyTriggeredNow = anyCheckTriggered(
            visibleChecks as any,
            exclusionAnswers as Record<string, "yes" | "no">,
          );
          const pgdLabel = String(pgd.id).startsWith("protocol-") ? "protocol" : "PGD";

          return (
            <div
              className={`border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
                }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${isDarkMode ? "bg-white text-black" : "bg-black text-white"
                    }`}
                >
                  {answeredNodesCount + 1}
                </div>
                <h3
                  className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  {(() => {
                    const label = String(pgd.id).startsWith("protocol-") ? "Protocol" : "PGD";
                    let drugName: string | null = null;
                    try {
                      if (pgd.drugRef) drugName = getDrug(pgd.drugRef).name;
                    } catch {
                      drugName = null;
                    }
                    const condition = (pgd.indication || "").split(/[—–-]/)[0].trim();
                    const parts = [condition, drugName].filter(Boolean).join(" — ");
                    return `${label} pre-supply checks — ${parts} (${pgd.reference})`;
                  })()}
                </h3>
              </div>
              {!hasBothGroups && (
                <p className={`text-xs mb-4 ml-9 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  {inclusionChecks.length > 0
                    ? "All must be confirmed Yes to proceed."
                    : `Answer each — any "Yes" means the patient cannot be supplied under this ${pgdLabel}.`}
                </p>
              )}

              {(() => {
                const renderCheck = (check: any) => {
                  const prefilled = prefilledExclusionIds.has(check.id);
                  return (
                    <div
                      key={check.id}
                      className={`flex items-center justify-between gap-3 p-3 border rounded-lg ${isDarkMode ? "border-gray-700" : "border-gray-200"
                        }`}
                    >
                      <div className="flex-1">
                        <div>
                          <p className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"
                            }`}>
                            {check.question}
                            {check.hint && (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="ml-1 inline-flex align-[-2px]"
                                      aria-label={check.hint}
                                    >
                                      <CircleHelp
                                        className={`h-3.5 w-3.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"
                                          }`}
                                      />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    align="center"
                                    collisionPadding={16}
                                    className="max-w-72 text-xs leading-relaxed"
                                  >
                                    {check.hint}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </p>
                        </div>
                        {check.linkedVitalType && onSaveVitals && (
                          <VitalsModal
                            vitalType={check.linkedVitalType as any}
                            onSave={onSaveVitals}
                            isDarkMode={isDarkMode}
                            trigger={
                              <button
                                type="button"
                                className={`mt-1 inline-flex items-center gap-1 text-xs underline ${isDarkMode ? "text-blue-300" : "text-blue-700"
                                  }`}
                              >
                                <Thermometer className="h-3 w-3" />
                                Add {check.linkedVitalType} reading
                              </button>
                            }
                          />
                        )}
                        {prefilled && (
                          <span
                            className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full border ${isDarkMode
                                ? "border-blue-700 text-blue-300 bg-blue-950/40"
                                : "border-blue-300 text-blue-800 bg-blue-50"
                              }`}
                          >
                            Answered earlier
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {(["yes", "no"] as const).map((opt) => {
                          const selected = exclusionAnswers[check.id] === opt;
                          const baseCls =
                            "h-9 w-14 inline-flex items-center justify-center rounded-md border text-sm font-medium transition select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1";
                          // Inclusion criteria must be Yes, exclusion criteria
                          // must be No — colour the safe answer green either way.
                          const isInclusion = (check as any).direction === "exclude_on_no";
                          let stateCls: string;
                          if (selected) {
                            const isSafe = isInclusion ? opt === "yes" : opt === "no";
                            if (isSafe) {
                              stateCls = isDarkMode
                                ? "border-green-600 bg-green-950 text-green-100"
                                : "border-green-300 bg-green-50 text-green-900";
                            } else {
                              stateCls = isDarkMode
                                ? "border-red-600 bg-red-950 text-red-100"
                                : "border-red-300 bg-red-50 text-red-900";
                            }
                          } else {
                            stateCls = isDarkMode
                              ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
                              : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50";
                          }
                          return (
                            <button
                              key={`${check.id}-${opt}`}
                              type="button"
                              aria-pressed={selected}
                              onClick={(e) => {
                                setExclusionAnswers((p) => ({
                                  ...p,
                                  [check.id]: opt,
                                }));
                                (e.currentTarget as HTMLButtonElement).blur();
                              }}
                              className={`${baseCls} ${stateCls}`}
                            >
                              {opt === "yes" ? "Yes" : "No"}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                };

                return hasBothGroups ? (
                  <div className="space-y-4 mt-3">
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ml-0 ${isDarkMode ? "text-emerald-400" : "text-emerald-700"}`}>
                        Inclusion criteria — all must be Yes
                      </p>
                      <p className={`text-xs mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        Confirm the patient meets each criterion.
                      </p>
                      <div className="space-y-2">
                        {inclusionChecks.map(renderCheck)}
                      </div>
                    </div>
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? "text-red-400" : "text-red-700"}`}>
                        Exclusion criteria — all must be No
                      </p>
                      <p className={`text-xs mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        Any "Yes" means supply cannot proceed under this {pgdLabel}.
                      </p>
                      <div className="space-y-2">
                        {exclusionChecks.map(renderCheck)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {visibleChecks.map(renderCheck)}
                  </div>
                );
              })()}

              {anyTriggeredNow && (
                <Alert className="mt-4 border-amber-200 bg-amber-50 text-amber-900">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription>
                    One or more criteria not met — patient cannot be supplied under this {pgdLabel}.
                  </AlertDescription>
                </Alert>
              )}

              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => handleAnswer(exclusionAnswers)}
                  disabled={!allAnswered}
                >
                  Continue
                </Button>
              </div>
            </div>
          );
        })()}

        <PathwayFlowFooter
          showBack={!(pathway.refinedFlowUi && currentNode.type === "gateway_alarm") && (history.length > 0 || !!onCancel)}
          atStart={history.length === 0}
          onBack={handleStepBack}
          isDarkMode={isDarkMode}
          dock={showPersistentEligibilityPanel ? (
            <div>
              {previewEligibleDrugIds !== null && (
                <p className={`text-xs font-medium mb-1 px-1 ${
                  isDarkMode ? "text-amber-400" : "text-amber-600"
                }`}>
                  Preview — tap Continue to confirm
                </p>
              )}
              <PersistentEligibilityPanel
                currentEligibleDrugIds={
                  previewEligibleDrugIds ??
                  (eliminationPreview ? eliminationPreview.eligibleDrugIds : eligibleDrugIds)
                }
                initialEligibleDrugIds={pathway?.initialEligibleDrugIds ?? []}
                eliminationLog={
                  eliminationPreview
                    ? [...eliminationLog, ...eliminationPreview.logEntries]
                    : eliminationLog
                }
                drugLabels={drugLabels}
                isDarkMode={isDarkMode}
              />
            </div>
          ) : undefined}
        />
      </div>
    </div></FlowFooterProvider>
  );
}
